/**
 * Bamoa WebRTC-Hook (Welle 5) - Berater-Variante.
 *
 * Funktion:
 *  - Joint /rtc-Namespace, empfängt Offer von Stele, sendet Answer
 *  - Empfängt DataChannel 'bamoa-cam-ctrl' für Capability/ROI/Scan-Events
 *  - Exponiert sendCamCommand() um Befehle zur Stele zu schicken
 *  - Liefert Camera-Capabilities und ROI-State für die UI
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { loadIceServers } from '../lib/iceServers';
import { io, Socket } from 'socket.io-client';
import {
  CamCommand,
  CamEvent,
  CAM_DATACHANNEL_LABEL,
  SCAN_CHUNK_SIZE,
} from './camControl';
import type { CameraCapabilities, RoiBox } from './cameraManager';
import { createPortraitVideoTrack, PortraitPipeline } from './portraitTrack';
import { readConnectionInfo, ConnectionInfo } from './connectionStats';
import { createE2eeSession, applySframe, e2eeForced, sframeSupported, requestEncoderKeyframe, getCryptoStats, E2eeSession } from './sframe';
import { preferVideoCodec, tuneVideoSender, preferH264InSdp } from '../lib/codecPrefs';
import {
  getPreferredDevice, setPreferredDevice, describeMediaError, getPreferredResolution,
} from '@/lib/mediaDevices';
import { useUIStore } from '@/state/store';   // Welle 8c-pre32: Live-Stats + Summary direkt in den Store
import { apiFetch } from '@/lib/auth';         // Welle 8c-pre32: Qualitätszusammenfassung server-seitig loggen

export interface TrackMeta {
  streamId: string;
  role: 'main' | 'preview' | 'audio';
  cameraRole?: 'webcam' | 'doccam';
  width?: number;
  height?: number;
  frameRate?: number;
}

export interface RoiInfo {
  roi: RoiBox;
  effectiveZoom: number;
  isDetail: boolean;
  cameraRole: 'webcam' | 'doccam';
}

import type { Signer, SignMarker } from '@/lib/signTypes';

export interface ScanResult {
  role: 'main' | 'preview';
  format: string;
  /** pre216: echter MIME-Typ (für beliebige Dateien, z. B. PDF). */
  mime?: string;
  /** pre216: Dateiname der übertragenen Datei. */
  name?: string;
  /** Welle 5b: Blob-URL (URL.createObjectURL) statt base64-data. */
  url?: string;
  error?: string;
  receivedAt: number;
}

/** Welle 8c-pre38: Dokumenten-Signierung (PDF über lokale SignController-API der Stele). */
export interface SignatureResult {
  sigId: string;
  status: 'uploading' | 'submitted' | 'signing' | 'received' | 'aborted' | 'error';
  url?: string;       // Blob-URL des signierten PDF (nur bei 'received')
  filename?: string;
  error?: string;
  receivedAt: number;
  progress?: number;  // Welle 8c-pre41: 0..100 für Upload bzw. Rückübertragung
  /** pre238: Identifizierungs-Nachweis des Gasts (WebAuthn/Passkey), falls angefordert. */
  identProof?: any;
  identStatus?: 'verified' | 'unavailable' | 'declined';
  /** pre239: vom Kunden eingegebener SMS-Code (zur Verifikation durch den Berater-Client). */
  otpCode?: string;
}

/** Welle 8c-pre: Geräte-Status der Gegenseite (Stele), gemeldet P2P über DataChannel. */
export interface RemoteDeviceStatus {
  hasCamera: boolean;
  hasMic: boolean;
  hasAudioOut: boolean;
  message?: string;
  receivedAt: number;
}

export interface RtcState {
  status: 'idle' | 'connecting' | 'waiting-offer' | 'negotiating' | 'connected' | 'error' | 'ended';
  error?: string;
  /** Welle 8c-pre: Geräteproblem der Stele (z.B. keine Kamera), beim Berater anzuzeigen. */
  remoteDeviceStatus: RemoteDeviceStatus | null;
  /** Welle 8c-pre: Geräte mid-call wechseln. */
  switchVideoInput: (deviceId: string) => Promise<void>;
  switchAudioInput: (deviceId: string) => Promise<void>;
  /** Welle 8c: Bildschirmfreigabe aus dieser Session an die Stele (E2E-verschlüsselt
   *  über denselben Video-Sender). startScreenShare gibt false zurück, wenn der
   *  Nutzer den Auswahldialog abbricht. */
  screenSharing: boolean;
  startScreenShare: () => Promise<boolean>;
  stopScreenShare: () => Promise<void>;
  localStream: MediaStream | null;
  /** Alias für localStream - rückwärtskompatibel zu Welle 4f */
  localStreamMain: MediaStream | null;
  /** Welle 6a: Portrait-Output (was tatsächlich an Stele gesendet wird). Berater zeigt das im Self-View. */
  localPortraitStream: MediaStream | null;
  remoteStream: MediaStream | null;
  remoteStreamMain: MediaStream | null;
  remoteStreamPreview: MediaStream | null;
  remoteStreamAudio: MediaStream | null;
  /** Welle 8d / Mesh M1: Experten-Strecke (Magic-Link-Gast), transport-verschlüsselt */
  remoteGuestStream: MediaStream | null;
  guestName: string | null;
  /** Welle 8d/M2: zugeschalteten Experten gezielt trennen (ohne den Call zu beenden). */
  disconnectGuest: () => void;
  remoteMainMeta: TrackMeta | null;
  remotePreviewMeta: TrackMeta | null;
  hasPreview: boolean;
  /** Aktuelle Camera-Capabilities, indexiert nach Rolle. */
  capabilities: Record<'main' | 'preview', CameraCapabilities | null>;
  /** Aktueller ROI-State pro Rolle. */
  roiState: Record<'main' | 'preview', RoiInfo | null>;
  /** Zuletzt empfangener Scan. */
  lastScan: ScanResult | null;
  /** Welle 8c-pre38: Status/Ergebnis der Dokumenten-Signierung. */
  lastSignature: SignatureResult | null;
  /** Welle 8c-pre38: PDF zur Stele schicken und dort signieren lassen. Gibt sigId zurück. */
  submitSignature: (file: File, markers?: SignMarker[], signers?: Signer[], requireIdent?: boolean, requireOtp?: boolean) => Promise<string | null>;
  /** Welle 8c-pre38: laufenden Signiervorgang abbrechen (löst auf der Stele /Sign/getkill aus). */
  abortSignature: () => void;
  /** Welle 7a: Verbindungs- & Verschlüsselungsinfo aus getStats. */
  connectionInfo: ConnectionInfo | null;
  /** Welle 8c-pre30: Live- + kumulierte Call-Statistiken fürs In-Call-Overlay. */
  liveStats: LiveStats | null;
  hangup: (reason?: string) => void;
  /** Welle 7e: Aufzeichnungs-Status an die Stele melden. */
  sendRecordingState: (active: boolean, topic?: string) => void;
  /** Welle 7e/pre97: aktive Aufzeichnungs-Zustimmung auf der Stele anfordern (Pflicht, Touch). */
  requestRecordingConsent: (topic: string, mode: 'hinweis' | 'einwilligung', timeoutMs?: number) => Promise<{ accepted: boolean; at: number; cancelled?: boolean }>;
  /** Welle 7e/pre98: laufende Zustimmungs-Anfrage abbrechen (Berater) → Stele schließt das Sheet. */
  cancelRecordingConsent: () => void;
  toggleMic: () => boolean;
  toggleCamera: () => boolean;
  requestSwap: () => void;
  /** Welle 8d/M2: Vorschau-Stream (360p) an/aus — Bandbreite fürs Hauptbild sparen. */
  previewEnabled: boolean;
  setPreviewEnabled: (on: boolean) => void;
  /** Befehl an Stele schicken (Zoom, ROI, Scan). */
  sendCamCommand: (cmd: CamCommand) => boolean;
}

interface UseWebRtcOpts {
  rtcToken: string | null;
  role: 'agent' | 'stele';
  steleId?: string | null;
  onPeerGone?: (reason: string) => void;
}

/**
 * Welle 8c-pre5: Video-Encoder zu einem sofortigen Keyframe zwingen.
 *
 * generateKeyFrame() gehört zur RTCRtpScriptTransform-API und ist mit unserem
 * Legacy-createEncodedStreams-Pfad ein No-Op. Stattdessen wird die Encoding-Schicht
 * kurz deaktiviert und reaktiviert — der Encoder startet neu und sendet als ersten
 * Frame zwingend ein Keyframe. Das ist die zuverlässige Methode für Insertable Streams.
 * Kurzer Freeze (~1 Frame) auf der Gegenseite, danach sofort sauberes Bild.
 */
/** Welle 8c-pre41: Jitter-Buffer-Ziel der Video-Empfänger. Für den Kiosk-Betrieb gilt
 *  Aktualität vor Glätte: ein zu großes Ziel führt dazu, dass der Buffer bei Jitter
 *  anwächst und Frames „nachgeholt" werden (Verzögerung + Freezes), obwohl netzseitig
 *  kaum Verlust ist. Default 0 → es wird immer das aktuellste Frame gezeigt, verspätete
 *  Frames werden verworfen statt nachgespielt. Mit ?jbt=<ms> auf das alte verlust-
 *  resilientere Verhalten (z. B. 150) zurückschaltbar. Gilt nur für Video; Audio behält
 *  seinen Default-Buffer. */
const RX_JITTER_TARGET_MS = (() => {
  try {
    const p = new URLSearchParams(window.location.search).get('jbt');
    if (p !== null) { const n = parseInt(p, 10); if (!Number.isNaN(n)) return Math.max(0, n); }
  } catch { /* ignore */ }
  return 0;
})();
function tuneReceiverForLoss(receiver: RTCRtpReceiver, kind: string) {
  if (kind !== 'video') return;
  try {
    const anyR = receiver as any;
    if ('jitterBufferTarget' in anyR) { anyR.jitterBufferTarget = RX_JITTER_TARGET_MS; return; }
    if ('playoutDelayHint' in anyR) { anyR.playoutDelayHint = RX_JITTER_TARGET_MS / 1000; }
  } catch { /* ignore */ }
}

async function forceVideoKeyframe(pc: RTCPeerConnection): Promise<void> {
  const senders = pc.getSenders().filter((s) => s.track?.kind === 'video' && s.track.readyState === 'live');
  // Welle 8c-pre23: Läuft auf den Video-Sendern ein Worker-Transform, erzeugen wir den
  // Keyframe sauber via generateKeyFrame() (kein Encoder-Aus/Ein, kein Mikro-Ruckeln).
  const w = window as any;
  const usingWorker = typeof w.RTCRtpScriptTransform !== 'undefined'
    && senders.some((s) => (s as any).transform instanceof w.RTCRtpScriptTransform);
  if (usingWorker) { requestEncoderKeyframe(); return; }
  // Fallback: Encoder kurz aus-/einschalten erzwingt einen Keyframe.
  for (const sender of senders) {
    try {
      const params = sender.getParameters();
      if (!params.encodings || params.encodings.length === 0) params.encodings = [{}];
      const wasActive = params.encodings[0].active !== false;
      params.encodings[0].active = false;
      await sender.setParameters(params);
      await new Promise((r) => setTimeout(r, 50));
      const p2 = sender.getParameters();
      if (p2.encodings && p2.encodings[0]) p2.encodings[0].active = wasActive;
      await sender.setParameters(p2);
    } catch (e) {
      console.warn('[rtc] forceVideoKeyframe fehlgeschlagen', e);
    }
  }
}

// Welle 8c-pre25: Diagnose. Eine kompakte [diag]-Zeile pro Intervall mit den
// entscheidenden Zahlen, um „E2EE an = korrupt" einzugrenzen:
//  - crypto ok/fail/pass: wie viele Frames pro Sekunde sauber entschlüsselt vs.
//    verworfen (Magic da, AES-Auth fehlgeschlagen) vs. Klartext durchgereicht.
//    Hohe fail-Rate auf sauberem Pfad ⇒ der zusammengesetzte Frame stimmt nicht
//    (Verdacht: Codec-Header mitverschlüsselt → Packetizer/Reassembly kaputt).
//  - rx lost/pli: echter Netz-Paketverlust. Niedrig + hohe crypto-fail ⇒ NICHT Netz.
//  - rx dec vs tx enc: wie viele Frames es bis zum Decoder schaffen.
//  - pair: host/srflx/relay (+ relayProtocol) — der „kein Relay"-Check.
interface DiagPrev {
  t: number; rxDec: number; rxDrop: number; rxRecv: number; rxKf: number;
  rxLost: number; rxPli: number; rxNack: number; rxBytes: number; rxFreeze: number;
  txEnc: number; txKf: number; txBytes: number;
  cEnc: number; cOk: number; cFail: number; cPass: number;
}

/** Welle 8c-pre30: kompakte Live- + kumulierte Kennzahlen.
 *  Live = aktuelles 3s-Intervall, kumuliert = ganzer Call (getStats-Zähler sind
 *  monoton, der pc lebt pro Call → der letzte Wert IST die Call-Summe). */
export interface LiveStats {
  // Live (aktuelles Intervall)
  rxFps: number; txFps: number;
  rxKbps: number; txKbps: number;
  width: number; height: number; codec: string;
  rttMs: number | null; jitterMs: number | null;
  path: string; relay: string | null;
  // Welle 8c-pre31: Qualitäts-Ampel dieser Seite (live bewertet)
  level: QualityLevel;
  // Kumuliert (ganzer Call)
  callSeconds: number;
  packetsLost: number; packetsReceived: number; lossPct: number;
  freezeCount: number; freezeSeconds: number;
  nackTotal: number; pliTotal: number; keyFramesTotal: number;
  peakRttMs: number | null; avgRttMs: number | null; maxJitterMs: number | null;
  minKbps: number | null; maxKbps: number | null;
  bandwidthLimitedSec: number; cpuLimitedSec: number;
  everRelayed: boolean; cryptoFailTotal: number;
}
interface StatsAccum {
  callStart: number; peakRtt: number; maxJitter: number; minKbps: number; maxKbps: number; everRelayed: boolean;
}

/** Welle 8c-pre31: Qualitäts-Ampel. 'bad' soll im UI rot blinken. */
export type QualityLevel = 'good' | 'fair' | 'bad';

/** Welle 8c-pre31: kompakte, übertragbare Kennzahlen EINER Seite. Die Stele schickt
 *  ihre Werte in diesem Format über den cam-ctrl-DataChannel an den Berater, damit die
 *  Verbindungsqualitäts-Kachel BEIDE Seiten zeigt und ein Stele-Problem als solches
 *  erkennbar wird. Die Empfangsseite (rx) ist die aussagekräftige: was diese Seite an
 *  Video tatsächlich bekommt. */
export interface PeerStats {
  side: 'agent' | 'stele';
  level: QualityLevel;
  rttMs: number | null; jitterMs: number | null;
  lossPct: number;        // kumuliert
  rxKbps: number; rxFps: number;
  width: number; height: number; codec: string;
  freezeCount: number;    // kumuliert
  relay: boolean;
  // Welle 8c-pre32: kumulierte Aggregate über den ganzen Call — Basis für die
  // End-of-Call-Zusammenfassung. Beide Seiten füllen diese Felder (Stele über einen
  // eigenen kleinen Akkumulator, Berater aus seiner LiveStats-Akkumulation).
  peakRttMs: number | null; avgRttMs: number | null; maxJitterMs: number | null;
  freezeSeconds: number; packetsLost: number;
  callSeconds: number;
}

/** Welle 8c-pre32: Qualitätszusammenfassung eines beendeten Gesprächs (beide Seiten).
 *  Wird in der Wrap-Ansicht angezeigt und server-seitig je Call geloggt/persistiert. */
export interface CallSummary {
  durationSec: number;
  worst: QualityLevel;
  agent: PeerStats | null;  // was der Berater empfangen hat (Gast-Video)
  stele: PeerStats | null;  // was die Stele empfangen hat (Berater-Video)
}

export function liveStatsToPeer(ls: LiveStats): PeerStats {
  return {
    side: 'agent', level: ls.level,
    rttMs: ls.rttMs, jitterMs: ls.jitterMs, lossPct: ls.lossPct,
    rxKbps: ls.rxKbps, rxFps: ls.rxFps, width: ls.width, height: ls.height, codec: ls.codec,
    freezeCount: ls.freezeCount, relay: ls.everRelayed,
    peakRttMs: ls.peakRttMs, avgRttMs: ls.avgRttMs, maxJitterMs: ls.maxJitterMs,
    freezeSeconds: ls.freezeSeconds, packetsLost: ls.packetsLost,
    callSeconds: ls.callSeconds,
  };
}

/** Welle 8c-pre32: Gesamt-Urteil einer Seite aus den KUMULIERTEN Werten (für die
 *  End-of-Call-Zusammenfassung, anders als die Live-Ampel, die Intervall-Deltas nutzt). */
export function summaryVerdict(s: PeerStats | null): QualityLevel {
  if (!s) return 'good';
  if (s.lossPct >= 3 || s.freezeCount > 0 || (s.peakRttMs != null && s.peakRttMs > 500)) return 'bad';
  if (s.relay || s.lossPct >= 1 || (s.peakRttMs != null && s.peakRttMs > 250)) return 'fair';
  return 'good';
}
function worseLevel(a: QualityLevel, b: QualityLevel): QualityLevel {
  if (a === 'bad' || b === 'bad') return 'bad';
  if (a === 'fair' || b === 'fair') return 'fair';
  return 'good';
}

/** Welle 8c-pre32: Defense-in-depth — auch der Berater (Answerer) entfernt den
 *  Dependency-Descriptor-extmap aus seinem Answer-SDP. Der festgezurrte Fix sitzt
 *  primär im Stele-Offer (Offerer bestimmt die Aushandlung), aber so wirbt der Berater
 *  selbst dann nie mit DD, falls künftig doch einmal ein Offer mit DD eintrifft.
 *  ?dd=keep behält ihn (Vergleichstest). */
function stripDependencyDescriptor(sdp: string): string {
  try {
    return sdp
      .split('\r\n')
      .filter((line) => !(line.startsWith('a=extmap:') && line.includes('dependency-descriptor')))
      .join('\r\n');
  } catch { return sdp; }
}
/** Welle 8c-pre32: warnt, wenn der festgezurrte Default (kein DD im SDP) verletzt ist. */
function assertNoDependencyDescriptor(sdp: string | null | undefined, ddStrip: boolean, label: string): void {
  if (!ddStrip || !sdp) return;
  if (sdp.includes('dependency-descriptor')) {
    console.warn(`[rtc] ⚠ REGRESSION: Dependency-Descriptor trotz Strip noch im ${label}-SDP — Keyframe-Bug-Risiko!`);
  }
}

/** Welle 8c-pre31: Live-Bewertung einer Seite aus Intervall-Deltas + kumulierten Werten. */
function gradeQuality(opts: {
  freezeDelta: number; cryptoFailDelta: number; lossPct: number; rttMs: number | null; relay: boolean;
}): QualityLevel {
  const { freezeDelta, cryptoFailDelta, lossPct, rttMs, relay } = opts;
  if (freezeDelta > 0 || cryptoFailDelta > 0 || lossPct >= 3 || (rttMs != null && rttMs > 500)) return 'bad';
  if (relay || lossPct >= 1 || (rttMs != null && rttMs > 250)) return 'fair';
  return 'good';
}

function logRtcDiag(
  stats: RTCStatsReport,
  crypto: { enc: number; decOk: number; decFail: number; decPass: number },
  prevRef: { current: DiagPrev | null },
  label: string,
  accumRef?: { current: StatsAccum | null },
): LiveStats | null {
  const byId = new Map<string, any>();
  let rx: any = null, tx: any = null, pair: any = null;
  stats.forEach((r: any) => {
    byId.set(r.id, r);
    if (r.type === 'inbound-rtp' && r.kind === 'video') rx = r;
    else if (r.type === 'outbound-rtp' && r.kind === 'video') tx = r;
    else if (r.type === 'candidate-pair' && (r.nominated || r.selected)) pair = r;
  });
  let codec = '?';
  if (rx?.codecId && byId.get(rx.codecId)) codec = String(byId.get(rx.codecId).mimeType || '').replace('video/', '');
  let path = '?', relay = '';
  let relayed = false;
  if (pair) {
    const lc = byId.get(pair.localCandidateId), rc = byId.get(pair.remoteCandidateId);
    path = `${lc?.candidateType ?? '?'}/${rc?.candidateType ?? '?'}`;
    if (lc?.relayProtocol) relay = ` relay:${lc.relayProtocol}`;
    relayed = lc?.candidateType === 'relay' || rc?.candidateType === 'relay' || !!lc?.relayProtocol;
  }
  const now = Date.now();
  const cur: DiagPrev = {
    t: now,
    rxDec: rx?.framesDecoded ?? 0, rxDrop: rx?.framesDropped ?? 0, rxRecv: rx?.framesReceived ?? 0,
    rxKf: rx?.keyFramesDecoded ?? 0, rxLost: rx?.packetsLost ?? 0, rxPli: rx?.pliCount ?? 0, rxNack: rx?.nackCount ?? 0,
    rxBytes: rx?.bytesReceived ?? 0, rxFreeze: rx?.freezeCount ?? 0,
    txEnc: tx?.framesEncoded ?? 0, txKf: tx?.keyFramesEncoded ?? 0, txBytes: tx?.bytesSent ?? 0,
    cEnc: crypto.enc, cOk: crypto.decOk, cFail: crypto.decFail, cPass: crypto.decPass,
  };
  const p = prevRef.current;
  prevRef.current = cur;
  const dt = p ? (now - p.t) / 1000 : 0;
  const ps = (c: number, q: number) => Math.round((c - q) / dt);
  const rttSec = pair?.currentRoundTripTime;
  const rttMs = rttSec != null ? Math.round(rttSec * 1000) : null;

  if (p && dt > 0) {
    const brKbps = Math.round(((cur.txBytes - p.txBytes) * 8 / dt) / 1000);
    console.log(
      `[diag:${label}] rx: dec=${ps(cur.rxDec, p.rxDec)}/s drop=${ps(cur.rxDrop, p.rxDrop)}/s recv=${ps(cur.rxRecv, p.rxRecv)}/s `
      + `kf=${cur.rxKf - p.rxKf} lost=${cur.rxLost - p.rxLost} pli=${cur.rxPli - p.rxPli} nack=${cur.rxNack - p.rxNack} `
      + `${rx?.frameWidth ?? '?'}x${rx?.frameHeight ?? '?'} ${codec}`
      + ` | crypto: ok=${ps(cur.cOk, p.cOk)}/s fail=${ps(cur.cFail, p.cFail)}/s pass=${ps(cur.cPass, p.cPass)}/s enc=${ps(cur.cEnc, p.cEnc)}/s`
      + ` | tx: enc=${ps(cur.txEnc, p.txEnc)}/s kf=${cur.txKf - p.txKf} ${brKbps}kbps`
      + ` | pair: ${path}${relay} rtt=${rttMs ?? '?'}ms`,
    );
  }

  if (!accumRef) return null;
  // Akkumulator (Spitzen/Min über den Call) initialisieren/fortschreiben
  if (!accumRef.current) accumRef.current = { callStart: now, peakRtt: 0, maxJitter: 0, minKbps: Infinity, maxKbps: 0, everRelayed: false };
  const acc = accumRef.current;
  if (rttMs != null) acc.peakRtt = Math.max(acc.peakRtt, rttMs);
  if (relayed) acc.everRelayed = true;
  let rxKbps = 0, txKbps = 0, rxFps = 0, txFps = 0;
  if (p && dt > 0) {
    txKbps = Math.round(((cur.txBytes - p.txBytes) * 8 / dt) / 1000);
    rxKbps = Math.round(((cur.rxBytes - p.rxBytes) * 8 / dt) / 1000);
    txFps = ps(cur.txEnc, p.txEnc);
    rxFps = ps(cur.rxDec, p.rxDec);
    if (txKbps > 0) { acc.minKbps = Math.min(acc.minKbps, txKbps); acc.maxKbps = Math.max(acc.maxKbps, txKbps); }
  }
  const pktLost = rx?.packetsLost ?? 0;
  const pktRecv = rx?.packetsReceived ?? 0;
  const lossDenom = pktLost + pktRecv;
  const lossPct = lossDenom > 0 ? Math.round((pktLost / lossDenom) * 1000) / 10 : 0;
  const qld = tx?.qualityLimitationDurations ?? {};
  const avgRtt = (pair?.responsesReceived > 0 && pair?.totalRoundTripTime != null)
    ? Math.round((pair.totalRoundTripTime / pair.responsesReceived) * 1000) : null;
  const jitterMs = rx?.jitter != null ? Math.round(rx.jitter * 1000) : null;
  if (jitterMs != null) acc.maxJitter = Math.max(acc.maxJitter, jitterMs);
  const freezeDelta = p ? (cur.rxFreeze - p.rxFreeze) : 0;
  const cryptoFailDelta = p ? (cur.cFail - p.cFail) : 0;
  const level = gradeQuality({ freezeDelta, cryptoFailDelta, lossPct, rttMs, relay: relayed });
  return {
    rxFps, txFps, rxKbps, txKbps,
    width: rx?.frameWidth ?? 0, height: rx?.frameHeight ?? 0, codec,
    rttMs, jitterMs, path, relay: relay ? relay.trim().replace('relay:', '') : null,
    level,
    callSeconds: Math.round((now - acc.callStart) / 1000),
    packetsLost: pktLost, packetsReceived: pktRecv, lossPct,
    freezeCount: rx?.freezeCount ?? 0,
    freezeSeconds: Math.round((rx?.totalFreezesDuration ?? 0) * 10) / 10,
    nackTotal: rx?.nackCount ?? 0, pliTotal: rx?.pliCount ?? 0,
    keyFramesTotal: rx?.keyFramesDecoded ?? 0,
    peakRttMs: acc.peakRtt || null, avgRttMs: avgRtt, maxJitterMs: acc.maxJitter || null,
    minKbps: acc.minKbps === Infinity ? null : acc.minKbps,
    maxKbps: acc.maxKbps || null,
    bandwidthLimitedSec: Math.round(qld.bandwidth ?? 0),
    cpuLimitedSec: Math.round(qld.cpu ?? 0),
    everRelayed: acc.everRelayed, cryptoFailTotal: crypto.decFail,
  };
}

/** Welle 8c Phase A: Peer-Einträge aus rtc:joined tragen jetzt { peerId, role,
 *  companion }. Der A/V-Berater spricht ausschließlich mit der Stele — daher nur
 *  den Stele-Peer übernehmen und Companion-/Agent-Peers ignorieren (sonst würde
 *  ein hinzukommender Companion peerIdRef kapern). Abwärtskompatibel zu reinen
 *  String-IDs (frühere Gateway-Version). */
type PeerEntry = { peerId: string; role?: string; companion?: boolean } | string;
function pickStelePeerId(peers: unknown): string | null {
  if (!Array.isArray(peers)) return null;
  for (const p of peers as PeerEntry[]) {
    if (typeof p === 'string') return p; // alte Gateway-Version: nur die Stele war je Peer
    if (p && p.role === 'stele') return p.peerId;
  }
  return null;
}

export function useWebRtc({ rtcToken, role, onPeerGone }: UseWebRtcOpts): RtcState {
  const [status, setStatus] = useState<RtcState['status']>('idle');
  const [error, setError] = useState<string | undefined>();
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [localPortraitStream, setLocalPortraitStream] = useState<MediaStream | null>(null);
  // Welle 8c-pre: Geräteproblem-Meldung der Stele (kommt P2P über den DataChannel)
  const [remoteDeviceStatus, setRemoteDeviceStatus] = useState<RemoteDeviceStatus | null>(null);
  // Welle 8c-pre: aktueller Roh-Video-Stream (für Kamera-Wechsel mid-call)
  const videoRawRef = useRef<MediaStream | null>(null);
  const [connectionInfo, setConnectionInfo] = useState<ConnectionInfo | null>(null);
  const [liveStats, setLiveStats] = useState<LiveStats | null>(null); // Welle 8c-pre30
  const [remoteStreamMain, setRemoteStreamMain] = useState<MediaStream | null>(null);
  const [remoteStreamPreview, setRemoteStreamPreview] = useState<MediaStream | null>(null);
  const [remoteStreamAudio, setRemoteStreamAudio] = useState<MediaStream | null>(null);
  // Welle 8d / Mesh M1: Experten-Strecke (Magic-Link-Gast)
  const [remoteGuestStream, setRemoteGuestStream] = useState<MediaStream | null>(null);
  const [guestName, setGuestName] = useState<string | null>(null);
  const [remoteMainMeta, setRemoteMainMeta] = useState<TrackMeta | null>(null);
  const [remotePreviewMeta, setRemotePreviewMeta] = useState<TrackMeta | null>(null);
  const [hasPreview, setHasPreview] = useState(false);
  // Welle 8d/M2: Vorschau-Stream (kleines 360p-Bild) an/aus — der Berater kann ihn bei
  // schlechter Verbindung abschalten, um Bandbreite fürs Hauptbild freizugeben.
  const [previewEnabled, setPreviewEnabledState] = useState(true);
  const [capabilities, setCapabilities] = useState<RtcState['capabilities']>({ main: null, preview: null });
  const [roiState, setRoiState] = useState<RtcState['roiState']>({ main: null, preview: null });
  const [lastScan, setLastScan] = useState<ScanResult | null>(null);
  const [lastSignature, setLastSignature] = useState<SignatureResult | null>(null);

  const pcRef = useRef<RTCPeerConnection | null>(null);
  const socketRef = useRef<Socket | null>(null);
  // Welle 8b: Socket-ID des A/V-Peers für targeted signaling (gezieltes Routing)
  const peerIdRef = useRef<string | null>(null);
  const dcRef = useRef<RTCDataChannel | null>(null);        // Kontrollkanal zur STELE
  const guestDcRef = useRef<RTCDataChannel | null>(null);   // pre254: Kontrollkanal zum Link-Gast (Mesh) — getrennt, damit Stele-Kommandos nie den Gast erreichen (Bugfix Licht/Kamerawechsel)
  const pendingIceRef = useRef<RTCIceCandidateInit[]>([]);
  const localStreamRef = useRef<MediaStream | null>(null);
  const remoteStreamsRef = useRef<Map<string, MediaStream>>(new Map());
  // Welle 7e/pre97: offener Resolver für die Stele-Zustimmungs-Antwort
  const consentResolverRef = useRef<((r: { accepted: boolean; at: number; cancelled?: boolean }) => void) | null>(null);
  // Welle 8d / Mesh M1: eigener PC-Slot für den Experten (additiv, berührt pcRef nicht)
  const guestPcRef = useRef<RTCPeerConnection | null>(null);
  const guestPeerIdRef = useRef<string | null>(null);
  const guestPendingIceRef = useRef<RTCIceCandidateInit[]>([]);
  const meshEnabled = (() => {
    try { return new URL(window.location.href).searchParams.get('mesh') !== '0'; } catch { return true; }
  })();
  const remoteMetaRef = useRef<Map<string, TrackMeta>>(new Map());
  const peerGoneFiredRef = useRef(false);
  const onPeerGoneRef = useRef(onPeerGone);
  onPeerGoneRef.current = onPeerGone;

  // Welle 6b: Scan-Chunking
  const scanBufferRef = useRef<{
    scanId: string;
    role: 'main' | 'preview';
    format: string;
    mime?: string;
    name?: string;
    totalBytes: number;
    totalChunks: number;
    chunks: ArrayBuffer[];
    receivedBytes: number;
  } | null>(null);
  const lastScanUrlRef = useRef<string | null>(null);

  // Welle 8c-pre38: Signatur-Empfang (signiertes PDF; Transport wie Scan: start → Chunks → end).
  const sigBufferRef = useRef<{
    sigId: string;
    filename: string;
    totalBytes: number;
    totalChunks: number;
    chunks: ArrayBuffer[];
    receivedBytes: number;
  } | null>(null);
  const lastSigUrlRef = useRef<string | null>(null);
  const sigCurrentIdRef = useRef<string | null>(null);
  const lastSigFilenameRef = useRef<string | null>(null);
  // Welle 8c-pre41: offener Upload-ACK-Warter (Stele bestätigt vollständigen Upload-Empfang).
  const sigSubmitAckRef = useRef<Map<string, () => void>>(new Map());

  // Welle 6: Berater Portrait-Pipeline
  const portraitRef = useRef<PortraitPipeline | null>(null);
  // pre93: Orientierung der Gegenstelle (true = 16:9 landscape → Berater sendet Vollbild).
  const remoteLandscapeRef = useRef(false);
  // Welle 7a: getStats-Polling-Timer
  const statsTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Welle 8c-pre16: Freeze-Watchdog — pro eingehendem Video-SSRC merken, ob der Decoder
  // trotz Datenfluss steht (Artefakte, die sonst nie weggehen → Keyframe anfordern).
  const inboundStatsRef = useRef<Map<number, { f: number; p: number; pli: number; kf: number }>>(new Map());
  const lastKeyframeReqRef = useRef<number>(0);
  const diagPrevRef = useRef<DiagPrev | null>(null); // Welle 8c-pre25: Diagnose-Vorwerte
  const statsAccumRef = useRef<StatsAccum | null>(null); // Welle 8c-pre30: kumulierte Call-Kennzahlen
  // Welle 8c-pre32: letzte bekannte Werte beider Seiten — beim Teardown daraus die
  // End-of-Call-Qualitätszusammenfassung bauen.
  const lastAgentStatsRef = useRef<LiveStats | null>(null);
  const lastSteleStatsRef = useRef<PeerStats | null>(null);
  const summarySentRef = useRef(false);
  const codecLockWarnedRef = useRef(false); // Welle 8c-pre32: VP9-Codec-Check nur einmal pro Call warnen
  const weakCipherWarnedRef = useRef(false); // pre207: schwache-Cipher-Warnung nur einmal pro Call
  const iceStartRef = useRef<number | null>(null); // Welle 8c-pre35: Startzeit der ICE-Sammlung (Kandidaten-Timing)
  // Welle 8c-pre21: periodischer Keyframe als Absicherung gegen nicht erkennbare Korruption
  const kfTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  // Welle 8c-pre17: maximale Robustheit — Medium ist die Wahrheit, Socket ist wegwerfbar.
  const mediaEverConnectedRef = useRef(false);
  const mediaDeadTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Welle 8c-pre18: Reconnect-Karenz bei transientem bye (Reload/Unload der Gegenseite)
  const byeGraceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Welle 7c/7e: SFrame-Session wird bei Capability angelegt; ENGAGEMENT steuert der
  // server-autoritative Intent (Tenant-Policy via rtc:joined), Test-Override via e2eeForced().
  const e2eeRef = useRef<E2eeSession | null>(null);
  const e2eeIntentRef = useRef<boolean>(false);

  // Welle 8c-pre32: beim Gesprächsende eine Qualitätszusammenfassung (beide Seiten)
  // in den Store schreiben (Wrap-Ansicht) und server-seitig je Call loggen/persistieren.
  const buildAndReportSummary = () => {
    if (summarySentRef.current) return;
    const agent = lastAgentStatsRef.current ? liveStatsToPeer(lastAgentStatsRef.current) : null;
    const stele = lastSteleStatsRef.current ?? null;
    if (!agent && !stele) return; // nie verbunden gewesen → nichts zu berichten
    summarySentRef.current = true;
    const durationSec = Math.max(agent?.callSeconds ?? 0, stele?.callSeconds ?? 0);
    const worst = worseLevel(summaryVerdict(agent), summaryVerdict(stele));
    const summary: CallSummary = { durationSec, worst, agent, stele };
    // 1) UI: in den Store → Wrap-Ansicht zeigt die Zusammenfassung
    try { useUIStore.getState().setCallSummary(summary); } catch {}
    // 2) Server: je Call loggen/persistieren (callId aus dem Store)
    const callId = (useUIStore.getState() as any).activeCallId ?? (window as any).__activeCallId ?? null;
    if (callId) {
      void apiFetch(`/api/calls/${callId}/quality`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ summary }),
      }, { noLogoutOn401: true }).then((r) => {
        if (!r.ok) console.warn('[rtc] quality-POST fehlgeschlagen', r.status);
      }).catch((e) => console.warn('[rtc] quality-POST Fehler', e));
    } else {
      console.warn('[rtc] kein activeCallId → Qualitätszusammenfassung nicht server-seitig geloggt');
    }
  };

  const teardown = (reason: string) => {
    if (peerGoneFiredRef.current) return;
    peerGoneFiredRef.current = true;
    console.log(`[rtc] teardown reason=${reason}`);
    // Welle 8c-pre32: aus den letzten bekannten Werten beider Seiten eine
    // Qualitätszusammenfassung bauen → Store (Wrap-Ansicht) + server-seitig loggen.
    try { buildAndReportSummary(); } catch (e) { console.warn('[rtc] Summary fehlgeschlagen', e); }
    try { socketRef.current?.emit('rtc:bye'); } catch {}
    try { dcRef.current?.close(); } catch {}
    dcRef.current = null;
    try { guestDcRef.current?.close(); } catch { /* */ }
    guestDcRef.current = null;
    try { socketRef.current?.disconnect(); } catch {}
    socketRef.current = null;
    peerIdRef.current = null;
    try { pcRef.current?.close(); } catch {}
    pcRef.current = null;
    // Welle 8d / Mesh M1: Experten-Strecke mit beenden.
    try { guestPcRef.current?.close(); } catch {}
    guestPcRef.current = null;
    guestPeerIdRef.current = null;
    guestPendingIceRef.current = [];
    setRemoteGuestStream(null);
    setGuestName(null);
    try { portraitRef.current?.stop(); } catch {}
    portraitRef.current = null;
    if (statsTimerRef.current) { clearInterval(statsTimerRef.current); statsTimerRef.current = null; }
    if (kfTimerRef.current) { clearInterval(kfTimerRef.current); kfTimerRef.current = null; }
    if (mediaDeadTimerRef.current) { clearTimeout(mediaDeadTimerRef.current); mediaDeadTimerRef.current = null; }
    if (byeGraceTimerRef.current) { clearTimeout(byeGraceTimerRef.current); byeGraceTimerRef.current = null; }
    setConnectionInfo(null);
    setLiveStats(null);
    statsAccumRef.current = null;
    diagPrevRef.current = null;
    localStreamRef.current?.getTracks().forEach((t) => { try { t.stop(); } catch {} });
    localStreamRef.current = null;
    videoRawRef.current?.getTracks().forEach((t) => { try { t.stop(); } catch {} });
    videoRawRef.current = null;
    setRemoteDeviceStatus(null);
    if (lastScanUrlRef.current) {
      try { URL.revokeObjectURL(lastScanUrlRef.current); } catch {}
      lastScanUrlRef.current = null;
    }
    scanBufferRef.current = null;
    remoteStreamsRef.current.clear();
    remoteMetaRef.current.clear();
    remoteLandscapeRef.current = false; // pre93: Orientierung zurücksetzen
    setLocalStream(null);
    setLocalPortraitStream(null);
    setRemoteStreamMain(null);
    setRemoteStreamPreview(null);
    setRemoteStreamAudio(null);
    setRemoteMainMeta(null);
    setRemotePreviewMeta(null);
    setCapabilities({ main: null, preview: null });
    setRoiState({ main: null, preview: null });
    setStatus('ended');
    onPeerGoneRef.current?.(reason);
  };

  function refreshRemoteAssignment() {
    let main: MediaStream | null = null;
    let preview: MediaStream | null = null;
    let audio: MediaStream | null = null;
    let mainMeta: TrackMeta | null = null;
    let previewMeta: TrackMeta | null = null;
    remoteMetaRef.current.forEach((meta, streamId) => {
      const s = remoteStreamsRef.current.get(streamId);
      if (!s) return;
      if (meta.role === 'main') { main = s; mainMeta = meta; }
      else if (meta.role === 'preview') { preview = s; previewMeta = meta; }
      else if (meta.role === 'audio') { audio = s; }
    });
    // Default-Fallback für Streams ohne Meta
    remoteStreamsRef.current.forEach((s, streamId) => {
      if (remoteMetaRef.current.has(streamId)) return;
      const hasAudio = s.getAudioTracks().length > 0;
      const hasVideo = s.getVideoTracks().length > 0;
      if (hasVideo && !main) {
        main = s;
        const vt = s.getVideoTracks()[0];
        const vs = vt.getSettings();
        mainMeta = {
          streamId, role: 'main',
          width: vs.width, height: vs.height, frameRate: vs.frameRate,
        };
      }
      if (hasAudio && !audio) {
        audio = s;
      }
    });
    setRemoteStreamMain(main);
    setRemoteStreamPreview(preview);
    setRemoteStreamAudio(audio);
    setRemoteMainMeta(mainMeta);
    setRemotePreviewMeta(previewMeta);
    setHasPreview(Boolean(preview));
  }

  const handleCamEvent = useCallback((evt: CamEvent) => {
    switch (evt.type) {
      case 'camera.capabilities': {
        // Capability-Liste → indexieren über aktuelle main/preview-Zuordnung
        const next: { main: CameraCapabilities | null; preview: CameraCapabilities | null } = { main: null, preview: null };
        for (const cam of evt.cameras ?? []) {
          let assignedRole: 'main' | 'preview' | null = null;
          remoteMetaRef.current.forEach((m) => {
            if (m.cameraRole === cam.cameraRole && (m.role === 'main' || m.role === 'preview')) {
              assignedRole = m.role;
            }
          });
          if (assignedRole === 'main') next.main = cam;
          else if (assignedRole === 'preview') next.preview = cam;
        }
        setCapabilities(next);
        console.log('[rtc] capabilities updated', next);
        break;
      }
      case 'camera.roi.updated': {
        setRoiState((prev) => ({
          ...prev,
          [evt.role]: {
            roi: evt.roi,
            effectiveZoom: evt.effectiveZoom,
            isDetail: evt.isDetail,
            cameraRole: evt.cameraRole,
          },
        }));
        break;
      }
      // scan-Events werden im DataChannel-Handler direkt verarbeitet (Welle 5b chunking)
      case 'light.state': {
        // Welle 8c-pre49: Stele bestätigt den geschalteten Licht-Zustand → an die UI.
        window.dispatchEvent(new CustomEvent('bamoa:light-state', {
          detail: { on: evt.on, ok: evt.ok, error: evt.error },
        }));
        break;
      }
    }
  }, []);

  // pre93: Orientierung der Gegenstelle erkennen. Ist das Hauptbild der Gegenstelle 16:9
  // quer (z. B. Gruppenlink-Gast statt Hochkant-Stele), sendet der Berater sein VOLLES Bild
  // statt des Hochkant-Crops. Reagiert auch live, wenn der Gast das Gerät dreht (resize).
  useEffect(() => {
    const stream = remoteStreamMain;
    if (!stream || stream.getVideoTracks().length === 0) return;
    const v = document.createElement('video');
    v.muted = true; v.playsInline = true; v.srcObject = stream;
    v.style.cssText = 'position:absolute;left:-10000px;width:1px;height:1px';
    document.body.appendChild(v);
    const apply = () => {
      const w = v.videoWidth, h = v.videoHeight;
      if (w <= 0 || h <= 0) return;
      const landscape = w > h;
      if (remoteLandscapeRef.current === landscape) return;
      remoteLandscapeRef.current = landscape;
      portraitRef.current?.setLandscape(landscape);
      console.log(`[rtc] Gegenstelle ${w}x${h} → Berater sendet ${landscape ? 'VOLLBILD (16:9)' : 'Hochkant'}`);
    };
    v.addEventListener('loadedmetadata', apply);
    v.addEventListener('resize', apply);
    v.play().catch(() => { /* hidden detector */ });
    apply();
    return () => {
      v.removeEventListener('loadedmetadata', apply);
      v.removeEventListener('resize', apply);
      try { v.srcObject = null; v.remove(); } catch { /* ignore */ }
    };
  }, [remoteStreamMain]);

  useEffect(() => {
    console.log('[rtc] useEffect run, rtcToken=', Boolean(rtcToken), 'role=', role);
    if (!rtcToken) return;
    console.log('[rtc] starte Setup');
    peerGoneFiredRef.current = false;
    // Welle 8c-pre32: Summary-Zustand für den neuen Call zurücksetzen
    summarySentRef.current = false;
    lastAgentStatsRef.current = null;
    lastSteleStatsRef.current = null;
    codecLockWarnedRef.current = false;
    weakCipherWarnedRef.current = false;
    // Welle 8c-pre32: aktive Codec-Konfiguration einmal loggen (Default festgezurrt).
    try {
      const q = new URL(window.location.href).searchParams;
      const vp9 = q.get('vp9') !== '0';
      const ddStrip = q.get('dd') !== 'keep';
      console.log(`[rtc] Codec-Default (festgezurrt): vp9=${vp9 ? 'on' : 'off'} dd=${ddStrip ? 'strip' : 'keep'} — Overrides: ?vp9=0 ?dd=keep`);
    } catch { /* ignore */ }
    let cancelled = false;

    (async () => {
      try {
        setStatus('connecting');
        const iceServers = await loadIceServers();

        // Welle 7c: Insertable Streams nur aktivieren wenn E2EE-Flag gesetzt —
        // sonst exakt die bisherige Konfiguration.
        const pcConfig: RTCConfiguration = { iceServers };
        // Welle 8c-pre35: Kandidaten vorab sammeln (srflx früher verfügbar → schneller direkt).
        pcConfig.iceCandidatePoolSize = 2;
        if (sframeSupported()) (pcConfig as any).encodedInsertableStreams = true;
        const pc = new RTCPeerConnection(pcConfig);
        iceStartRef.current = Date.now(); // Welle 8c-pre35: Referenz für Kandidaten-Timing
        pcRef.current = pc;

        // Welle 7c/7e: SFrame-Session anlegen, sobald der Browser Insertable Streams
        // kann. Verschlüsselt wird erst, wenn der Intent (Tenant-Policy) es will —
        // die Transforms reichen bis dahin unverändert durch.
        e2eeRef.current = null;
        if (sframeSupported()) {
          try {
            e2eeRef.current = await createE2eeSession();
            console.log('[rtc] SFrame-Session bereit (Capability vorhanden)');
          } catch (err) {
            console.warn('[rtc] E2EE-Setup fehlgeschlagen, fahre ohne SFrame fort', err);
            e2eeRef.current = null;
          }
        }

        // pre206: cam-ctrl-Kanal-Setup an einer Stelle — Stele erzeugt ihn (wir empfangen),
        // für Gäste erzeugen WIR ihn (s. gpc unten). Gleicher Handler für beide.
        const setupCamChannel = (channel: RTCDataChannel, kind: 'stele' | 'guest' = 'stele') => {
            // pre254: getrennte Registrierung — vorher überschrieb jeder Kanal denselben dcRef
            // (Last-Writer-Wins), wodurch Stele-Kommandos beim falschen Peer landeten.
            if (kind === 'guest') guestDcRef.current = channel; else dcRef.current = channel;
            // Welle 5b: binary mode für Snapshot-Chunks
            channel.binaryType = 'arraybuffer';
            channel.onopen = () => console.log('[rtc] dc open (agent)');
            channel.onclose = () => console.log('[rtc] dc closed (agent)');
            channel.onmessage = (msg) => {
              if (typeof msg.data === 'string') {
                try {
                  const evt = JSON.parse(msg.data) as CamEvent;
                  // Welle 8b: NFC kommt jetzt P2P über diesen DataChannel (nicht
                  // mehr über den Server). Kartendaten erreichen den Server nie.
                  if ((evt as any).type === 'nfc') {
                    console.log('[rtc] nfc empfangen (P2P), success=', (evt as any).payload?.success);
                    window.dispatchEvent(new CustomEvent('bamoa:nfc', { detail: (evt as any).payload }));
                    return;
                  }
                  // Welle 8c-pre31/32: periodische Stele-Statistik (P2P) → direkt in den Store
                  // (Verbindungsqualitäts-Kachel beider Seiten) + für die End-of-Call-Summary merken.
                  if ((evt as any).type === 'stats') {
                    const payload = (evt as any).payload as PeerStats;
                    if (payload) {
                      lastSteleStatsRef.current = payload;
                      const ui = useUIStore.getState();
                      ui.setCallStatsStele(payload);
                      if (payload.rttMs != null) ui.pushRttStele(payload.rttMs);
                    }
                    return;
                  }
                  // Welle 8c-pre16: Stele (Freeze-Watchdog) bittet uns um einen Keyframe
                  // → lokalen Encoder zwingen, damit der Decoder der Stele sich erholt.
                  if ((evt as any).type === 'camera.keyframe') {
                    if (pcRef.current) void forceVideoKeyframe(pcRef.current);
                    return;
                  }
                  // Welle 8c-pre: Geräte-Status der Stele (P2P), beim Berater anzeigen
                  if ((evt as any).type === 'device-status') {
                    const d = evt as any;
                    console.log('[rtc] Stele device-status:', d.hasCamera, d.hasMic, d.hasAudioOut, d.message ?? '');
                    setRemoteDeviceStatus({
                      hasCamera: !!d.hasCamera, hasMic: !!d.hasMic, hasAudioOut: !!d.hasAudioOut,
                      message: d.message, receivedAt: Date.now(),
                    });
                    return;
                  }
                  // Welle 5b: Snapshot-Header/Footer-Events lokal verarbeiten,
                  // alles andere geht in den allgemeinen handler
                  if (evt.type === 'camera.scan.start') {
                    scanBufferRef.current = {
                      scanId: evt.scanId,
                      role: evt.role,
                      format: evt.format,
                      mime: evt.mime,
                      name: evt.name,
                      totalBytes: evt.totalBytes,
                      totalChunks: evt.totalChunks,
                      chunks: [],
                      receivedBytes: 0,
                    };
                    console.log(`[rtc] scan empfangen, erwarte ${evt.totalBytes} B in ${evt.totalChunks} Chunks`);
                  } else if (evt.type === 'camera.scan.end') {
                    const sb = scanBufferRef.current;
                    if (!sb || sb.scanId !== evt.scanId) {
                      console.warn('[rtc] scan.end ohne passenden buffer');
                      return;
                    }
                    // Chunks zusammensetzen
                    const merged = new Uint8Array(sb.totalBytes);
                    let offset = 0;
                    for (const c of sb.chunks) {
                      merged.set(new Uint8Array(c), offset);
                      offset += c.byteLength;
                    }
                    const mime = sb.mime || (sb.format === 'png' ? 'image/png'
                      : sb.format === 'webp' ? 'image/webp'
                      : sb.format === 'pdf' ? 'application/pdf' : 'image/jpeg');
                    const blob = new Blob([merged], { type: mime });
                    // Alte URL freigeben falls vorhanden
                    if (lastScanUrlRef.current) {
                      try { URL.revokeObjectURL(lastScanUrlRef.current); } catch {}
                    }
                    const url = URL.createObjectURL(blob);
                    lastScanUrlRef.current = url;
                    setLastScan({
                      role: sb.role,
                      format: sb.format,
                      mime,
                      name: sb.name,
                      url,
                      receivedAt: Date.now(),
                    });
                    scanBufferRef.current = null;
                    console.log(`[rtc] scan komplett, ${(sb.totalBytes / 1024).toFixed(1)} KB → ${url}`);
                  } else if (evt.type === 'camera.scan.error') {
                    setLastScan({
                      role: evt.role,
                      format: '',
                      error: evt.error,
                      receivedAt: Date.now(),
                    });
                    scanBufferRef.current = null;
                  } else if (evt.type === 'sign.received') {
                    setLastSignature((prev) => ({
                      sigId: evt.sigId, status: 'submitted',
                      filename: prev?.sigId === evt.sigId ? prev.filename : lastSigFilenameRef.current ?? undefined,
                      receivedAt: Date.now(),
                    }));
                  } else if (evt.type === 'sign.signing') {
                    setLastSignature((prev) => ({
                      sigId: evt.sigId, status: 'signing',
                      filename: prev?.sigId === evt.sigId ? prev.filename : lastSigFilenameRef.current ?? undefined,
                      receivedAt: Date.now(),
                    }));
                  } else if (evt.type === 'sign.submit.ack') {
                    const r = sigSubmitAckRef.current.get(evt.sigId);
                    if (r) { r(); sigSubmitAckRef.current.delete(evt.sigId); }
                  } else if (evt.type === 'sign.result.start') {
                    sigBufferRef.current = {
                      sigId: evt.sigId, filename: evt.filename,
                      totalBytes: evt.totalBytes, totalChunks: evt.totalChunks,
                      chunks: [], receivedBytes: 0,
                      identProof: evt.identProof, identStatus: evt.identStatus, otpCode: (evt as any).otpCode,
                    } as any;
                    setLastSignature((prev) => (prev && prev.sigId === evt.sigId
                      ? { ...prev, status: 'signing', progress: 0 } : prev));
                    console.log(`[rtc] signiertes PDF erwartet ${evt.totalBytes} B in ${evt.totalChunks} Chunks`);
                  } else if (evt.type === 'sign.result.end') {
                    const sg = sigBufferRef.current;
                    if (!sg || sg.sigId !== evt.sigId) { console.warn('[rtc] sign.result.end ohne Puffer'); return; }
                    if (sg.receivedBytes < sg.totalBytes) {
                      // Unvollständig → NICHT quittieren, Stele wird erneut senden.
                      console.warn(`[rtc] sign.result.end unvollständig (${sg.receivedBytes}/${sg.totalBytes})`);
                      sigBufferRef.current = null;
                      return;
                    }
                    const merged = new Uint8Array(sg.totalBytes);
                    let off = 0;
                    for (const c of sg.chunks) { merged.set(new Uint8Array(c), off); off += c.byteLength; }
                    const blob = new Blob([merged], { type: 'application/pdf' });
                    if (lastSigUrlRef.current) { try { URL.revokeObjectURL(lastSigUrlRef.current); } catch { /* */ } }
                    const url = URL.createObjectURL(blob);
                    lastSigUrlRef.current = url;
                    setLastSignature({ sigId: sg.sigId, status: 'received', url, filename: sg.filename, progress: 100, receivedAt: Date.now(), identProof: (sg as any).identProof, identStatus: (sg as any).identStatus, otpCode: (sg as any).otpCode });
                    sigBufferRef.current = null;
                    sigCurrentIdRef.current = null;
                    // Welle 8c-pre41: vollständigen Empfang bestätigen, damit die Stele nicht erneut sendet.
                    try { channel.send(JSON.stringify({ type: 'sign.result.ack', sigId: sg.sigId } as CamCommand)); } catch { /* ignore */ }
                    console.log(`[rtc] signiertes PDF komplett, ${(sg.totalBytes / 1024).toFixed(1)} KB → ${url}`);
                  } else if (evt.type === 'sign.aborted') {
                    setLastSignature({ sigId: evt.sigId, status: 'aborted', receivedAt: Date.now() });
                    sigBufferRef.current = null;
                    sigCurrentIdRef.current = null;
                  } else if (evt.type === 'sign.error') {
                    setLastSignature({ sigId: evt.sigId, status: 'error', error: evt.error, receivedAt: Date.now() });
                    sigBufferRef.current = null;
                    sigCurrentIdRef.current = null;
                  } else {
                    handleCamEvent(evt);
                  }
                } catch (err) {
                  console.warn('[rtc] dc invalid event', err);
                }
              } else if (msg.data instanceof ArrayBuffer) {
                // Binary chunk - akkumulieren. Welle 8c-pre37: zuerst Signatur, sonst Scan.
                const sg = sigBufferRef.current;
                if (sg) {
                  sg.chunks.push(msg.data);
                  sg.receivedBytes += msg.data.byteLength;
                  // Welle 8c-pre41: Fortschritt der Rückübertragung (gedrosselt auf ganze %)
                  if (sg.totalBytes > 0) {
                    const pct = Math.min(100, Math.round((sg.receivedBytes / sg.totalBytes) * 100));
                    setLastSignature((prev) => (prev && prev.sigId === sg.sigId && prev.progress !== pct
                      ? { ...prev, progress: pct } : prev));
                  }
                  return;
                }
                const sb = scanBufferRef.current;
                if (!sb) return;
                sb.chunks.push(msg.data);
                sb.receivedBytes += msg.data.byteLength;
              }
            };
        };
        pc.ondatachannel = (e) => {
          console.log('[rtc] dc received', e.channel.label);
          if (e.channel.label === CAM_DATACHANNEL_LABEL) setupCamChannel(e.channel);
        };

        // Welle 8c-pre: zuletzt gewählte Quellen verbindlich (exact) anfordern, damit
        // wirklich das gespeicherte Gerät geöffnet wird. Fällt sauber auf Default zurück,
        // falls das Gerät weg oder belegt ist.
        const prefVideo = getPreferredDevice('videoinput');
        const prefAudio = getPreferredDevice('audioinput');
        const res = getPreferredResolution();
        const baseVideo = { width: { ideal: res.width }, height: { ideal: res.height }, frameRate: { ideal: 30 } };
        let rawStream: MediaStream;
        try {
          rawStream = await navigator.mediaDevices.getUserMedia({
            audio: prefAudio ? { deviceId: { exact: prefAudio } } : true,
            video: { ...(prefVideo ? { deviceId: { exact: prefVideo } } : {}), ...baseVideo },
          });
        } catch (e) {
          console.warn('[rtc] bevorzugtes Gerät nicht verfügbar, Fallback auf Default:', (e as Error).name);
          rawStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: baseVideo });
        }
        if (cancelled) { rawStream.getTracks().forEach((t) => t.stop()); return; }
        videoRawRef.current = rawStream;

        const audioTrack = rawStream.getAudioTracks()[0];
        const videoTrack = rawStream.getVideoTracks()[0];

        // Audio: direkt addTrack
        if (audioTrack) {
          pc.addTrack(audioTrack, rawStream);
        }

        // Video: durch Portrait-Pipeline
        const portrait = createPortraitVideoTrack(rawStream, {
          shortSide: 720, landscapeLongSide: 1280, fps: 30,
        });
        if (portrait) {
          portraitRef.current = portrait;
          // pre93: Falls die Gegenstelle schon als landscape erkannt wurde, sofort Vollbild senden.
          if (remoteLandscapeRef.current) portrait.setLandscape(true);
          pc.addTrack(portrait.outputTrack, portrait.outputStream);
          setLocalPortraitStream(portrait.outputStream);
        } else {
          // Fallback: kein Portrait-Crop, raw video direkt (Stele zeigt dann mit Letterboxing)
          console.warn('[rtc] portrait-pipeline scheiterte, fallback auf raw video');
          if (videoTrack) {
            pc.addTrack(videoTrack, rawStream);
            setLocalPortraitStream(rawStream);
          }
        }

        localStreamRef.current = rawStream;
        setLocalStream(rawStream);

        // Welle 7c: SFrame auf alle ausgehenden Sender verdrahten (encrypt).
        if (e2eeRef.current) {
          const sess = e2eeRef.current;
          for (const sender of pc.getSenders()) {
            if (sender.track) applySframe(sender, sess, 'encrypt');
          }
        }

        const onAvTrack = (e: RTCTrackEvent) => {
          const s = e.streams?.[0];
          if (!s) return;
          console.log('[rtc] ontrack streamId=', s.id, 'kind=', e.track.kind);
          tuneReceiverForLoss(e.receiver, e.track.kind);
          // Welle 7c: SFrame auf eingehenden Receiver verdrahten (decrypt).
          if (e2eeRef.current) {
            applySframe(e.receiver, e2eeRef.current, 'decrypt');
          }
          remoteStreamsRef.current.set(s.id, s);
          refreshRemoteAssignment();
        };
        const onAvIce = (e: RTCPeerConnectionIceEvent) => {
          if (e.candidate && socketRef.current) {
            const dt = iceStartRef.current ? Date.now() - iceStartRef.current : 0;
            console.log(`[ice:agent] +${dt}ms ${e.candidate.type ?? '?'} ${e.candidate.protocol ?? ''}`);
            socketRef.current.emit('rtc:ice', { candidate: e.candidate, to: peerIdRef.current });
          }
        };
        const onAvStateChange = () => {
          const pc = pcRef.current;
          if (!pc) return;
          console.log('[rtc] pc.state', pc.connectionState);
          if (pc.connectionState === 'connected') {
            const wasConnectedBefore = mediaEverConnectedRef.current;
            mediaEverConnectedRef.current = true;
            if (mediaDeadTimerRef.current) { clearTimeout(mediaDeadTimerRef.current); mediaDeadTimerRef.current = null; }
            setStatus('connected');
            // Welle 8c-pre18: Nach einer WIEDERVERBINDUNG (ICE-Restart/Reconnect) einen
            // frischen Keyframe erzwingen — sonst dekodiert der Decoder auf der verlorenen
            // Referenz weiter (bleibende Fragmente). Beidseitig: lokal + Anforderung an Stele.
            if (wasConnectedBefore) {
              console.log('[rtc] Wiederverbindung → Keyframe erzwingen (beidseitig)');
              void forceVideoKeyframe(pc);
              try { dcRef.current?.send(JSON.stringify({ type: 'camera.keyframe' } as CamCommand)); } catch { /* ignore */ }
            }
            // Welle 7a: Verbindungs-/Verschlüsselungsinfo pollen
            if (statsTimerRef.current) clearInterval(statsTimerRef.current);
            const poll = async () => {
              const cur = (guestPcRef.current && guestPcRef.current.connectionState === 'connected') ? guestPcRef.current : pcRef.current;
              if (!cur) return;
              const info = await readConnectionInfo(cur);
              // Welle 7c: E2EE-Status aus der SFrame-Session ergänzen
              info.e2eeActive = e2eeRef.current?.isActive() ?? false;
              info.e2eeIntended = e2eeIntentRef.current;
              info.e2eeSuspended = e2eeRef.current?.isSuspended() ?? false;
              setConnectionInfo(info);
              // Cipher-Verifikation: nur warnen wenn der Browser die Cipher kennt,
              // sie schwach ist UND keine E2EE-Schicht darüber liegt. Mit aktivem
              // SFrame sind die Medien AES-256-GCM — die SRTP-Cipher ist dann egal.
              if (info.connected && info.cipherKnown && !info.cipherStrong && !info.e2eeActive && !weakCipherWarnedRef.current) {
                weakCipherWarnedRef.current = true;
                console.warn('[rtc] Hinweis: Transport per DTLS-SRTP (ohne GCM/E2EE) — bei Nicht-E2EE-Calls (Gast) erwartet.', {
                  srtp: info.srtpCipher, dtls: info.dtlsCipher,
                });
              }
              // Welle 8c-pre16: Freeze-Watchdog. Steht ein eingehender Video-Decoder
              // trotz weiterhin ankommender Pakete (verlorene Referenz/Keyframe), fordern
              // wir aktiv einen Keyframe von der Stele an (camera.keyframe → Stele macht
              // forceVideoKeyframe). Sonst bleiben die Artefakte dauerhaft bestehen.
              try {
                const stats = await cur.getStats();
                let stalled = false;
                stats.forEach((r: any) => {
                  if (r.type === 'inbound-rtp' && r.kind === 'video') {
                    const prev = inboundStatsRef.current.get(r.ssrc);
                    const f = r.framesDecoded || 0, p = r.packetsReceived || 0;
                    const pli = r.pliCount || 0, kf = r.keyFramesDecoded || 0;
                    if (prev && f > 0) {
                      const decodeStalled = p > prev.p && f === prev.f;        // friert ein
                      const keyframeStuck = pli > prev.pli && kf === prev.kf;  // bittet um Keyframe, bekommt keinen → korrupt
                      if (decodeStalled || keyframeStuck) stalled = true;
                    }
                    inboundStatsRef.current.set(r.ssrc, { f, p, pli, kf });
                  }
                });
                if (stalled && Date.now() - lastKeyframeReqRef.current > 4000) {
                  lastKeyframeReqRef.current = Date.now();
                  console.warn('[rtc] Decoder steht trotz Datenfluss → Keyframe von Stele angefordert');
                  try { dcRef.current?.send(JSON.stringify({ type: 'camera.keyframe' } as CamCommand)); } catch { /* ignore */ }
                  void forceVideoKeyframe(cur);
                }
                const snap = logRtcDiag(stats, getCryptoStats(), diagPrevRef, 'agent', statsAccumRef); // Welle 8c-pre25/30
                if (snap) {
                  setLiveStats(snap);
                  // Welle 8c-pre32: Live-Werte des Beraters in den Store (Verbindungsqualitäts-Kachel)
                  lastAgentStatsRef.current = snap;
                  const ui = useUIStore.getState();
                  ui.setCallStatsAgent(liveStatsToPeer(snap));
                  if (snap.rttMs != null) ui.pushRttAgent(snap.rttMs);
                  // Welle 8c-pre32: Default-Verifikation — wenn nicht VP9 (und ?vp9=0 nicht
                  // gesetzt), einmalig warnen. Macht stilles Zurückfallen auf VP8 sichtbar.
                  if (!codecLockWarnedRef.current && snap.codec && snap.codec !== '?') {
                    let vp9On = true;
                    try { vp9On = new URL(window.location.href).searchParams.get('vp9') !== '0'; } catch { /* ignore */ }
                    if (vp9On && e2eeIntentRef.current && !/vp9/i.test(snap.codec)) {
                      codecLockWarnedRef.current = true;
                      console.warn(`[rtc] ⚠ REGRESSION: Empfangs-Codec ist ${snap.codec} statt VP9 — Keyframe-Bug-Risiko bei E2EE!`);
                    } else if (/vp9/i.test(snap.codec)) {
                      codecLockWarnedRef.current = true;
                    }
                  }
                }
              } catch { /* getStats-Fehler ignorieren */ }
            };
            poll();
            statsTimerRef.current = setInterval(poll, 3000);
            // Welle 8c-pre21: alle 8s einen Keyframe erzwingen (Absicherung gegen nicht
            // erkennbare Korruption). Heilt die Stele-Ansicht des Berater-Bildes spätestens
            // nach 8s, auch wenn kein Stats-Signal vorliegt.
            if (kfTimerRef.current) clearInterval(kfTimerRef.current);
            kfTimerRef.current = setInterval(() => {
              const c = pcRef.current;
              if (c && c.connectionState === 'connected') void forceVideoKeyframe(c);
            }, 8000);
          }
          // Welle 8c-pre17: 'disconnected'/'failed' beenden den Call NICHT sofort. Der
          // Berater (Answerer) wartet auf den ICE-Restart der Stele; transiente Hänger
          // erholt WebRTC selbst. Teardown erst nach langer Karenz ohne Erholung.
          if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
            if (!mediaDeadTimerRef.current) {
              mediaDeadTimerRef.current = setTimeout(() => {
                mediaDeadTimerRef.current = null;
                if (pcRef.current?.connectionState !== 'connected') teardown('media-dead');
              }, 25000);
            }
          }
          if (pc.connectionState === 'closed') teardown('peer-connection-closed');
        };
        pc.ontrack = onAvTrack;
        pc.onicecandidate = onAvIce;
        pc.onconnectionstatechange = onAvStateChange;

        // pre210: PeerConnection frisch aufbauen, aber Socket + Peer-ID BEHALTEN. Für
        // Folge-Calls: der iOS-Gast beendet oft ohne 'bye', die alte PC bleibt gebraucht →
        // Renegotiation bricht Berater-Audio + Steuerkanal. Ein frischer PC-Aufbau (gleicher
        // Socket, gleiche Medien) verhält sich wie der erste Call.
        const rebuildAvPc = async () => {
          try { pcRef.current?.close(); } catch { /* */ }
          const npc = new RTCPeerConnection(pcConfig);
          pcRef.current = npc;
          npc.ondatachannel = (e) => { if (e.channel.label === CAM_DATACHANNEL_LABEL) setupCamChannel(e.channel); };
          npc.ontrack = onAvTrack;
          npc.onicecandidate = onAvIce;
          npc.onconnectionstatechange = onAvStateChange;
          const raw = videoRawRef.current;
          if (raw) {
            const at = raw.getAudioTracks()[0];
            if (at) npc.addTrack(at, raw);
            const pt = portraitRef.current;
            if (pt) npc.addTrack(pt.outputTrack, pt.outputStream);
            else { const vt = raw.getVideoTracks()[0]; if (vt) npc.addTrack(vt, raw); }
          }
          if (e2eeRef.current) { const sess = e2eeRef.current; for (const se of npc.getSenders()) if (se.track) applySframe(se, sess, 'encrypt'); }
          console.log('[rtc] PeerConnection für Folge-Call frisch aufgebaut');
        };

        const socket = io('/rtc', {
          path: '/socket.io', transports: ['websocket'], auth: { token: rtcToken },
        });
        socketRef.current = socket;

        socket.on('connect', () => console.log('[rtc] socket connected'));
        socket.on('connect_error', (e) => {
          console.warn('[rtc] connect_error', e.message);
          setError(`Socket: ${e.message}`);
        });

        // Welle 7c/7e: eigenen ECDH-Public-Key senden — nur wenn Intent es will.
        const sendE2eeKey = async () => {
          const sess = e2eeRef.current;
          if (!sess || !e2eeIntentRef.current) return;
          try {
            const pub = await sess.exportPublicKey();
            socket.emit('rtc:e2ee-key', { publicKey: pub, keyId: sess.keyId, to: peerIdRef.current });
            console.log('[rtc] E2EE Public Key gesendet');
          } catch (err) {
            console.warn('[rtc] E2EE Public Key senden fehlgeschlagen', err);
          }
        };

        // ── Welle 8d / Mesh M1: Experten-Slot (Berater offeriert dem Gast) ──────
        // Eigener PC, getrennt von pcRef. KEIN SFrame auf dieser Strecke (M1 →
        // transport-verschlüsselt, wie das bestehende „3+ Teilnehmer"-Verhalten).
        function closeGuest() {
          try { guestPcRef.current?.close(); } catch { /* ignore */ }
          guestPcRef.current = null;
          guestPeerIdRef.current = null;
          guestPendingIceRef.current = [];
          setRemoteGuestStream(null);
          setGuestName(null);
        }
        async function makeGuestOffer(guestId: string) {
          if (!meshEnabled) return;
          try {
            if (guestPcRef.current) { try { guestPcRef.current.close(); } catch { /* ignore */ } }
            // Welle 8d/M1: Die Gast-Strecke ist transport-verschlüsselt (DTLS-SRTP), KEIN
            // SFrame. pcConfig trägt aber encodedInsertableStreams=true (für die SFrame-
            // Pipeline der Stele-Strecke). Auf dem guestPc OHNE angehängten Transform
            // würde das die encodierten Frames in eine nicht ausgelesene Pipeline leiten
            // → es fließt KEIN Bild (in beide Richtungen), obwohl die Verbindung steht.
            // Deshalb hier eine saubere Config nur mit ICE-Servern.
            const gpc = new RTCPeerConnection({
              iceServers: pcConfig.iceServers,
              iceCandidatePoolSize: 2,
            });
            guestPcRef.current = gpc;
            guestPeerIdRef.current = guestId;
            const ls = localStreamRef.current;
            if (ls) for (const t of ls.getTracks()) gpc.addTrack(t, ls);
            // pre205: Gast-Peer (kein E2EE) → H264 bevorzugen, damit das Handy in Hardware encodiert.
            preferVideoCodec(gpc, 'H264');
            // pre207: Gast erzeugt den cam-ctrl-Kanal selbst → wir empfangen ihn (auch im Mesh).
            gpc.ondatachannel = (e) => { if (e.channel.label === CAM_DATACHANNEL_LABEL) setupCamChannel(e.channel, 'guest'); };
            gpc.ontrack = (e) => {
              console.log('[rtc][mesh] guest ontrack', e.track.kind, 'muted=', e.track.muted);
              const s = e.streams[0] || new MediaStream([e.track]);
              setRemoteGuestStream(s);
            };
            gpc.onicecandidate = (e) => {
              if (e.candidate) socket.emit('rtc:ice', { candidate: e.candidate, to: guestId });
            };
            gpc.onconnectionstatechange = () => {
              const st = gpc.connectionState;
              console.log('[rtc][mesh] guestPc state', st);
              if (st === 'failed' || st === 'closed') setRemoteGuestStream(null);
            };
            const offer = await gpc.createOffer();
            await gpc.setLocalDescription(offer);
            void tuneVideoSender(gpc);
            socket.emit('rtc:offer', { sdp: gpc.localDescription, to: guestId });
            console.log('[rtc][mesh] Offer an Experten gesendet', guestId);
          } catch (e) {
            console.warn('[rtc][mesh] makeGuestOffer fehlgeschlagen', e);
          }
        }

        socket.on('rtc:joined', (data) => {
          console.log('[rtc] joined room', JSON.stringify(data));
          // Welle 8c Phase A: nur den Stele-Peer als A/V-Gegenstelle übernehmen.
          const steleId = pickStelePeerId(data?.peers);
          if (steleId) peerIdRef.current = steleId;
          // Welle 8d / Mesh M1: bereits anwesenden Gast/Experten erkennen → offerieren.
          if (meshEnabled && Array.isArray(data?.peers)) {
            const g: any = data.peers.find((p: any) => p && typeof p === 'object' && p.guest);
            if (g?.peerId) {
              setGuestName(g.name || 'Experte');
              void makeGuestOffer(g.peerId);
            }
          }
          // Welle 7e: Intent server-autoritativ aus rtc:joined; Test-Override via e2eeForced()
          const forced = e2eeForced();
          e2eeIntentRef.current = forced ?? Boolean(data?.e2ee);
          console.log('[rtc] E2EE-Intent =', e2eeIntentRef.current, forced !== null ? '(override)' : '(tenant-policy)');
          // Welle 8c-pre17: Bei Socket-Reconnect mit noch gesundem Medium den Status
          // NICHT auf 'waiting-offer' zurücksetzen (es kommt kein neues Offer) — der Call
          // läuft ungestört weiter; nur Peer-ID/E2EE re-syncen.
          const mediaHealthy = mediaEverConnectedRef.current && pcRef.current?.connectionState === 'connected';
          if (!mediaHealthy) setStatus('waiting-offer');
          if (steleId) sendE2eeKey();
        });

        socket.on('rtc:peer-joined', (data: { peerId?: string; role?: string; companion?: boolean; guest?: boolean; name?: string }) => {
          console.log('[rtc] peer joined', JSON.stringify(data));
          // Welle 8d / Mesh M1: Experte tritt bei → eigener PC-Slot, pcRef unberührt.
          if (meshEnabled && data?.guest && data.peerId) {
            setGuestName(data.name || 'Experte');
            void makeGuestOffer(data.peerId);
            return;
          }
          // Companion (zweite Berater-Session) oder andere Agenten ignorieren — der
          // A/V-Berater verbindet sich nur mit der Stele.
          if (data?.role !== 'stele') return;
          // Welle 8c-pre18: Stele ist (wieder) da → Reconnect-Karenz abbrechen.
          if (byeGraceTimerRef.current) { clearTimeout(byeGraceTimerRef.current); byeGraceTimerRef.current = null; }
          if (data?.peerId) peerIdRef.current = data.peerId;
          sendE2eeKey();
        });

        // Welle 7c/7e: Peer-Public-Key empfangen → Shared Key ableiten, nur wenn Intent an
        socket.on('rtc:e2ee-key', async (data: { publicKey: string; keyId: number; from?: string }) => {
          if (!e2eeIntentRef.current) return;
          if (data.from) peerIdRef.current = data.from;
          console.log('[rtc] E2EE Peer-Key empfangen');
          await e2eeRef.current?.onPeerKey(data.publicKey);
          // Bestätigen, dass WIR den Schlüssel haben → der Peer darf verschlüsseln
          if (e2eeRef.current?.hasKey()) socket.emit('rtc:e2ee-ready', { to: peerIdRef.current });
        });

        // Welle 7e: Peer bestätigt Schlüssel → wir dürfen verschlüsseln (nie vorher)
        socket.on('rtc:e2ee-ready', () => {
          if (!e2eeIntentRef.current) return;
          e2eeRef.current?.markPeerReady();
        });

        // Welle 7e: Teilnehmerzahl-Wechsel → E2EE nahtlos pausieren/fortsetzen.
        // Bei 3+ Teilnehmern ist Ende-zu-Ende nicht mehr möglich, der Call läuft
        // aber unterbrechungsfrei weiter (Transport bleibt verschlüsselt).
        socket.on('rtc:e2ee-mode', (data: { active: boolean; participants: number }) => {
          console.log('[rtc] E2EE-Modus:', data.active ? 'aktiv' : 'pausiert', `(${data.participants} Teilnehmer)`);
          e2eeRef.current?.setMode(data.active);
        });

        // pre210: Offer-Verarbeitung gekapselt (nutzt pcRef.current, damit sie nach rebuildAvPc greift).
        const processPrimaryOffer = async (data: { sdp: any; from?: string }) => {
          const pc = pcRef.current!;
          if (data.from) peerIdRef.current = data.from;
          setStatus('negotiating');
          await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));
          for (const c of pendingIceRef.current) {
            await pc.addIceCandidate(new RTCIceCandidate(c)).catch(() => {});
          }
          pendingIceRef.current = [];
          // pre207: Nicht-E2EE-Peer (mobiler Gast) → H264 bevorzugen; VP9 kann iOS-Safari nicht.
          if (!e2eeIntentRef.current) preferVideoCodec(pc, 'H264');
          const answer = await pc.createAnswer();
          // Welle 8c-pre32: festgezurrter Default auch answerseitig absichern. Der
          // Offer der Stele trägt durch deren Strip ohnehin keinen DD-extmap (und ein
          // Answer kann keinen nicht-angebotenen extmap hinzufügen) — dieser Strip ist
          // bewusst defensiv und schützt gegen künftige Re-Offer-Pfade mit DD.
          let ddKeep = false;
          try { ddKeep = new URL(window.location.href).searchParams.get('dd') === 'keep'; } catch { /* ignore */ }
          let outSdp = answer.sdp || '';
          if (!ddKeep) {
            const stripped = stripDependencyDescriptor(outSdp);
            if (stripped !== outSdp) console.log('[rtc] Dependency-Descriptor-Extension aus Answer entfernt (festgezurrter Default)');
            outSdp = stripped;
          }
          // pre208: Nicht-E2EE-Peer (mobiler Gast) → H264 in der m-Zeile nach vorne (iOS kann kein VP9).
          if (!e2eeIntentRef.current) outSdp = preferH264InSdp(outSdp);
          await pc.setLocalDescription({ type: 'answer', sdp: outSdp });
          assertNoDependencyDescriptor(pc.localDescription?.sdp, !ddKeep, 'Answer');
          socket.emit('rtc:answer', { sdp: pc.localDescription, to: peerIdRef.current });
        };
        socket.on('rtc:offer', async (data: { sdp: any; from?: string }) => {
          console.log('[rtc] received offer');
          // pre210: Folge-Call → gebrauchte PeerConnection verwerfen und frisch aufbauen (Socket bleibt).
          if (mediaEverConnectedRef.current) {
            console.log('[rtc] Folge-Offer erkannt → PeerConnection wird frisch aufgebaut');
            await rebuildAvPc();
          }
          await processPrimaryOffer(data);
        });

        // Welle 8d / Mesh M1: Antwort des Experten auf das Berater-Offer (guestPc).
        socket.on('rtc:answer', async (data: { sdp: any; from?: string }) => {
          if (!data.from || data.from !== guestPeerIdRef.current) return;
          const gpc = guestPcRef.current;
          if (!gpc) return;
          try {
            await gpc.setRemoteDescription(new RTCSessionDescription(data.sdp));
            const buf = guestPendingIceRef.current;
            guestPendingIceRef.current = [];
            for (const c of buf) await gpc.addIceCandidate(new RTCIceCandidate(c)).catch(() => {});
            console.log('[rtc][mesh] Experten-Answer verarbeitet');
          } catch (e) {
            console.warn('[rtc][mesh] setRemoteDescription(answer) fehlgeschlagen', e);
          }
        });

        socket.on('rtc:ice', async (data) => {
          // Welle 8d / Mesh M1: ICE des Experten → guestPc (eigener Puffer).
          if (data.from && data.from === guestPeerIdRef.current) {
            const gpc = guestPcRef.current;
            if (gpc && gpc.remoteDescription) {
              await gpc.addIceCandidate(new RTCIceCandidate(data.candidate)).catch(() => {});
            } else {
              guestPendingIceRef.current.push(data.candidate);
            }
            return;
          }
          if (!pc.remoteDescription) {
            pendingIceRef.current.push(data.candidate);
            return;
          }
          await pc.addIceCandidate(new RTCIceCandidate(data.candidate)).catch((e) =>
            console.warn('[rtc] addIceCandidate failed', e),
          );
        });

        // Welle 8c Phase A: Nur abbauen, wenn der STELE-Peer geht. Trennt sich der
        // Companion (zweite Berater-Session), darf der A/V-Call NICHT abreißen — sein
        // peer-left/bye wird hier ignoriert.
        // Welle 8c-pre17: Ein Socket-Abbruch der Stele beendet den Call NICHT mehr. Das
        // P2P-Medium läuft weiter; Socket.IO verbindet neu (→ rtc:peer-joined re-synct die
        // Peer-ID). Nur bewusstes Auflegen (rtc:bye) oder echter Medien-Tod beendet ihn.
        socket.on('rtc:peer-left', (data: { peerId?: string }) => {
          // Welle 8d / Mesh M1: Experte verlässt den Call → nur guestPc schließen.
          if (data?.peerId && data.peerId === guestPeerIdRef.current) { closeGuest(); return; }
          if (data?.peerId && data.peerId !== peerIdRef.current) return; // Companion/anderer
          console.log('[rtc] peer-left (Socket-Drop) — Medium entscheidet, kein Teardown');
        });
        socket.on('rtc:bye', (data: { from?: string; transient?: boolean }) => {
          // Welle 8d / Mesh M1: Experte legt auf → nur guestPc schließen, Call läuft weiter.
          if (data?.from && data.from === guestPeerIdRef.current) { closeGuest(); return; }
          if (data?.from && data.from !== peerIdRef.current) return;
          // Welle 8c-pre18: transientes bye (Reload/Unload der Stele) → nicht sofort
          // beenden, sondern Reconnect-Karenz; kommt die Stele zurück (peer-joined),
          // wird sie gelöscht. Hartes bye (Auflegen) → sofort beenden.
          if (data?.transient) {
            console.log('[rtc] transientes bye — 12s Reconnect-Karenz');
            if (!byeGraceTimerRef.current) {
              byeGraceTimerRef.current = setTimeout(() => {
                byeGraceTimerRef.current = null;
                if (pcRef.current?.connectionState !== 'connected') teardown('peer-bye-timeout');
              }, 12000);
            }
            return;
          }
          teardown('peer-bye');
        });

        // Welle 8b: NFC läuft jetzt P2P über den cam-ctrl DataChannel (siehe oben),
        // NICHT mehr über den Server. Der alte rtc:nfc-Socket-Handler ist entfernt.

        socket.on('rtc:recording-consent-response', (data: { accepted: boolean; at?: number }) => {
          console.log('[rtc] recording-consent-response', data.accepted);
          const r = consentResolverRef.current; consentResolverRef.current = null;
          r?.({ accepted: Boolean(data.accepted), at: data.at ?? Date.now() });
        });

        socket.on('rtc:track-meta', (data: { tracks: TrackMeta[] }) => {
          console.log('[rtc] received track-meta', JSON.stringify(data));
          remoteMetaRef.current.clear();
          for (const t of data.tracks) remoteMetaRef.current.set(t.streamId, t);
          // Audio per Track-Kind erkennen
          remoteStreamsRef.current.forEach((s, id) => {
            if (!remoteMetaRef.current.has(id)) {
              const hasAudio = s.getAudioTracks().length > 0;
              const hasVideo = s.getVideoTracks().length > 0;
              if (hasAudio && !hasVideo) {
                remoteMetaRef.current.set(id, { streamId: id, role: 'audio' });
              }
            }
          });
          refreshRemoteAssignment();
          // Auch die capabilities-Zuordnung muss neu (Rollen tauschen):
          // Wir re-mappen die letzten Capabilities falls noch vorhanden
          setCapabilities((prev) => {
            const allCams: CameraCapabilities[] = [];
            if (prev.main) allCams.push(prev.main);
            if (prev.preview && prev.preview.deviceId !== prev.main?.deviceId) allCams.push(prev.preview);
            const next: RtcState['capabilities'] = { main: null, preview: null };
            for (const cam of allCams) {
              remoteMetaRef.current.forEach((m) => {
                if (m.cameraRole === cam.cameraRole && (m.role === 'main' || m.role === 'preview')) {
                  next[m.role as 'main' | 'preview'] = cam;
                }
              });
            }
            return next;
          });
        });
      } catch (e: any) {
        console.error('[rtc] setup failed', e);
        setError(describeMediaError(e));
        setStatus('error');
      }
    })();

    const onBeforeUnload = () => {
      // Welle 8c-pre18: Reload/Unload → transientes bye. Die Gegenseite gewährt eine
      // kurze Reconnect-Karenz, statt den Call sofort zu beenden.
      try { socketRef.current?.emit('rtc:bye', { transient: true }); } catch {}
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    window.addEventListener('pagehide', onBeforeUnload);

    return () => {
      cancelled = true;
      window.removeEventListener('beforeunload', onBeforeUnload);
      window.removeEventListener('pagehide', onBeforeUnload);
      // Welle 9: Verlässt der abgebende Berater den Call wegen einer Weiterleitung,
      // darf KEIN hartes bye gesendet werden — sonst beendet die Stele den Call,
      // bevor der Übernehmer beigetreten ist. Transientes bye → Reconnect-Karenz,
      // in der die Stele auf den neuen Agenten-Peer schwenkt.
      const handoff = (window as any).__transferLeaving === true;
      try { socketRef.current?.emit('rtc:bye', handoff ? { transient: true } : undefined); } catch {}
      if (handoff) (window as any).__transferLeaving = false;
      try { dcRef.current?.close(); } catch {}
      dcRef.current = null;
    try { guestDcRef.current?.close(); } catch { /* */ }
    guestDcRef.current = null;
      try { socketRef.current?.disconnect(); } catch {}
      socketRef.current = null;
      try { pcRef.current?.close(); } catch {}
      pcRef.current = null;
      try { guestPcRef.current?.close(); } catch {}
      guestPcRef.current = null;
      try { portraitRef.current?.stop(); } catch {}
      portraitRef.current = null;
      localStreamRef.current?.getTracks().forEach((t) => { try { t.stop(); } catch {} });
      localStreamRef.current = null;
    };
  }, [rtcToken, role, handleCamEvent]);

  const sendCamCommand = useCallback((cmd: CamCommand): boolean => {
    const dc = dcRef.current;
    if (!dc || dc.readyState !== 'open') {
      console.warn('[rtc] sendCamCommand: stele-dc not open');
      return false;
    }
    try {
      dc.send(JSON.stringify(cmd));
      return true;
    } catch (e) {
      console.warn('[rtc] sendCamCommand send failed', e);
      return false;
    }
  }, []);

  // Welle 8d/M2: Vorschau-Stream an/aus schalten. Schickt das Kommando an die Stele
  // (stoppt/startet den Preview-Sender) und merkt sich den Zustand für die UI.
  const setPreviewEnabled = useCallback((on: boolean) => {
    const dc = dcRef.current;
    if (dc && dc.readyState === 'open') {
      try { dc.send(JSON.stringify({ type: 'camera.preview.set', on } as CamCommand)); } catch { /* ignore */ }
    }
    setPreviewEnabledState(on);
  }, []);

  // Welle 8d/M2: Berater trennt den zugeschalteten Experten gezielt. Signalisiert dem
  // Gast (rtc:kick → er verabschiedet sich und sendet bye, sodass auch die Stele die
  // Strecke abbaut) und baut die lokale Experten-Strecke ab. Der Call selbst läuft weiter.
  const disconnectGuest = useCallback(() => {
    const gid = guestPeerIdRef.current;
    try { if (gid) socketRef.current?.emit('rtc:kick', { to: gid }); } catch { /* ignore */ }
    try { guestPcRef.current?.close(); } catch { /* ignore */ }
    guestPcRef.current = null;
    guestPeerIdRef.current = null;
    guestPendingIceRef.current = [];
    setRemoteGuestStream(null);
    setGuestName(null);
  }, []);

  // Welle 8c-pre38: PDF zur Stele schicken, dort über die lokale SignController-API signieren.
  const submitSignature = useCallback(async (file: File, markers?: SignMarker[], signers?: Signer[], requireIdent?: boolean, requireOtp?: boolean): Promise<string | null> => {
    // pre254v2: Signatur geht an die STELE, wenn vorhanden (dort wird unterschrieben); nur im reinen Handy-Call an den Gast.
    const dc = dcRef.current ?? guestDcRef.current;
    if (!dc || dc.readyState !== 'open') {
      console.warn('[rtc] submitSignature: dc not open');
      return null;
    }
    const sigId = `sig-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    sigCurrentIdRef.current = sigId;
    lastSigFilenameRef.current = file.name;
    setLastSignature({ sigId, status: 'uploading', filename: file.name, progress: 0, receivedAt: Date.now() });
    let buf: ArrayBuffer;
    try {
      buf = await file.arrayBuffer();
    } catch (e) {
      console.warn('[rtc] submitSignature: Datei nicht lesbar', e);
      setLastSignature({ sigId, status: 'error', error: 'upload-failed', filename: file.name, receivedAt: Date.now() });
      return null;
    }
    const totalBytes = buf.byteLength;
    const totalChunks = Math.ceil(totalBytes / SCAN_CHUNK_SIZE);
    // Welle 8c-pre41: Upload mit Quittung (sign.submit.ack) + Wiederholung. Backpressure
    // wartet statt abzubrechen; bleibt die Bestätigung aus, wird komplett erneut gesendet.
    const MAX_ATTEMPTS = 5;
    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      // auf offenen Kanal warten (max 30s) — readyState frisch über Hilfsvariable lesen
      const open = await (async () => {
        const t0 = Date.now();
        while (Date.now() - t0 < 30000) {
          const st: string = dc.readyState;
          if (st === 'open') return true;
          await new Promise((r) => setTimeout(r, 100));
        }
        return (dc.readyState as string) === 'open';
      })();
      if (!open) continue;
      const ackP = new Promise<boolean>((resolve) => {
        sigSubmitAckRef.current.set(sigId, () => resolve(true));
        window.setTimeout(() => {
          if (sigSubmitAckRef.current.has(sigId)) { sigSubmitAckRef.current.delete(sigId); resolve(false); }
        }, 15000);
      });
      let broke = false;
      try {
        dc.send(JSON.stringify({
          type: 'sign.submit.start', sigId, filename: file.name, totalBytes, totalChunks, chunkSize: SCAN_CHUNK_SIZE, signers, markers, requireIdent, requireOtp,
        } as CamCommand));
        const TARGET = 250 * 1024, THRESH = 48 * 1024, t0 = Date.now();
        let sent = 0;
        for (let i = 0; i < totalChunks; i++) {
          const s = i * SCAN_CHUNK_SIZE, en = Math.min(s + SCAN_CHUNK_SIZE, totalBytes);
          let waited = 0;
          while (dc.bufferedAmount > THRESH) {
            await new Promise((r) => setTimeout(r, 5));
            if (dc.readyState !== 'open') { broke = true; break; }
            if ((waited += 5) > 60000) { broke = true; break; }
          }
          if (broke || dc.readyState !== 'open') { broke = true; break; }
          dc.send(buf.slice(s, en));
          sent += en - s;
          const pct = totalBytes > 0 ? Math.min(100, Math.round((sent / totalBytes) * 100)) : 100;
          setLastSignature((prev) => (prev && prev.sigId === sigId && prev.progress !== pct
            ? { ...prev, progress: pct } : prev));
          const ahead = (sent / TARGET) * 1000 - (Date.now() - t0);
          if (ahead > 0) await new Promise((r) => setTimeout(r, Math.min(ahead, 40)));
        }
        if (!broke) dc.send(JSON.stringify({ type: 'sign.submit.end', sigId } as CamCommand));
      } catch {
        broke = true;
      }
      if (broke) { sigSubmitAckRef.current.delete(sigId); continue; }
      const acked = await ackP;
      if (acked) {
        setLastSignature((prev) => (prev && prev.sigId === sigId ? { ...prev, status: 'submitted', progress: 100 } : prev));
        return sigId;
      }
      console.warn(`[rtc] sign.submit.ack ausgeblieben (Versuch ${attempt}/${MAX_ATTEMPTS}) — sende erneut`);
    }
    setLastSignature({ sigId, status: 'error', error: 'upload-no-ack', filename: file.name, receivedAt: Date.now() });
    return null;
  }, []);

  const abortSignature = useCallback((): void => {
    const dc = dcRef.current;
    const sigId = sigCurrentIdRef.current;
    if (sigId && dc && dc.readyState === 'open') {
      try { dc.send(JSON.stringify({ type: 'sign.abort', sigId } as CamCommand)); } catch { /* ignore */ }
    }
    sigBufferRef.current = null;
    setLastSignature((prev) => (prev ? { ...prev, status: 'aborted', receivedAt: Date.now() } : null));
  }, []);

  // ── Welle 8c: Bildschirmfreigabe (A/V-Session) ───────────────────────────────
  // Der Bildschirm wird per replaceTrack auf den bestehenden Video-Sender gelegt.
  // SFrame bleibt am Sender erhalten → der geteilte Bildschirm ist E2E-verschlüsselt.
  // Beim Beenden wird der Kamera-Track wiederhergestellt. forceVideoKeyframe + die
  // Keyframe-Bitte an die Stele verhindern den Decoder-Drift beim Track-Wechsel.
  const screenStreamRef = useRef<MediaStream | null>(null);
  const camRestoreTrackRef = useRef<MediaStreamTrack | null>(null);
  const [screenSharing, setScreenSharing] = useState(false);

  const stopScreenShare = useCallback(async () => {
    const pc = pcRef.current;
    const restore = camRestoreTrackRef.current ?? portraitRef.current?.outputTrack ?? null;
    if (pc && restore) {
      const sender = pc.getSenders().find((s) => s.track?.kind === 'video');
      if (sender && sender.track !== restore) {
        try {
          await sender.replaceTrack(restore);
          void forceVideoKeyframe(pc);
          const dc = dcRef.current;
          if (dc && dc.readyState === 'open') {
            try { dc.send(JSON.stringify({ type: 'camera.keyframe' } as CamCommand)); } catch { /* ignore */ }
          }
        } catch (e) { console.warn('[rtc] Kamera-Wiederherstellung nach Screen-Share fehlgeschlagen', e); }
      }
    }
    // Self-View zurück auf die Kamera
    if (portraitRef.current?.outputStream) setLocalPortraitStream(portraitRef.current.outputStream);
    else if (videoRawRef.current) setLocalPortraitStream(videoRawRef.current);
    screenStreamRef.current?.getTracks().forEach((t) => { try { t.stop(); } catch { /* ignore */ } });
    screenStreamRef.current = null;
    camRestoreTrackRef.current = null;
    setScreenSharing(false);
    console.log('[rtc] Bildschirmfreigabe beendet');
  }, []);

  const startScreenShare = useCallback(async (): Promise<boolean> => {
    const pc = pcRef.current;
    if (!pc) return false;
    let screen: MediaStream;
    try {
      screen = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
    } catch (e) {
      console.warn('[rtc] Bildschirmfreigabe abgebrochen:', (e as Error).name);
      return false; // Nutzer hat den Auswahldialog abgebrochen o.ä.
    }
    const screenTrack = screen.getVideoTracks()[0];
    const sender = pc.getSenders().find((s) => s.track?.kind === 'video');
    if (!screenTrack || !sender) { screen.getTracks().forEach((t) => t.stop()); return false; }
    camRestoreTrackRef.current = sender.track ?? portraitRef.current?.outputTrack ?? null;
    screenStreamRef.current = screen;
    try {
      await sender.replaceTrack(screenTrack);
      void forceVideoKeyframe(pc);
      const dc = dcRef.current;
      if (dc && dc.readyState === 'open') {
        try { dc.send(JSON.stringify({ type: 'camera.keyframe' } as CamCommand)); } catch { /* ignore */ }
      }
    } catch (e) {
      console.warn('[rtc] replaceTrack (Bildschirm) fehlgeschlagen', e);
      screen.getTracks().forEach((t) => t.stop());
      screenStreamRef.current = null;
      camRestoreTrackRef.current = null;
      return false;
    }
    setLocalPortraitStream(screen);   // Self-View zeigt, was geteilt wird
    setScreenSharing(true);
    // „Freigabe beenden" über die Browser-eigene Leiste
    screenTrack.onended = () => { void stopScreenShare(); };
    console.log('[rtc] Bildschirmfreigabe gestartet');
    return true;
  }, [stopScreenShare]);

  return {
    status,
    error,
    remoteDeviceStatus,
    screenSharing,
    startScreenShare,
    stopScreenShare,
    async switchVideoInput(deviceId: string) {
      const pc = pcRef.current;
      if (!pc) return;
      try {
        const ns = await navigator.mediaDevices.getUserMedia({
          video: { deviceId: { exact: deviceId }, width: { ideal: 1280 }, height: { ideal: 720 }, frameRate: { ideal: 30 } },
        });
        const newVideo = ns.getVideoTracks()[0];
        if (!newVideo) { ns.getTracks().forEach((t) => t.stop()); return; }
        // Alte Pipeline + alten Roh-Video-Track sauber stoppen
        try { portraitRef.current?.stop(); } catch {}
        const ls = localStreamRef.current;
        ls?.getVideoTracks().forEach((t) => { try { ls.removeTrack(t); t.stop(); } catch {} });
        // Neue Portrait-Pipeline aus der neuen Quelle
        const portrait = createPortraitVideoTrack(ns, { shortSide: 720, landscapeLongSide: 1280, fps: 30 });
        let outTrack: MediaStreamTrack;
        if (portrait) { portraitRef.current = portrait; if (remoteLandscapeRef.current) portrait.setLandscape(true); outTrack = portrait.outputTrack; setLocalPortraitStream(portrait.outputStream); }
        else { outTrack = newVideo; setLocalPortraitStream(ns); }
        // replaceTrack erhält den SFrame-Transform am Sender → E2EE bleibt aktiv
        const sender = pc.getSenders().find((s) => s.track?.kind === 'video');
        if (sender) {
          await sender.replaceTrack(outTrack);
          // (a) Ausgehend: eigenes Keyframe erzwingen, damit die Stele-Sicht auf den
          //     Berater nicht driftet.
          void forceVideoKeyframe(pc);
          // (b) Eingehend: die Stele bitten, ihrerseits ein Keyframe zu senden — das
          //     Umschalten stallt kurz unseren Decode-Pfad, wodurch das EMPFANGENE
          //     Stele-Bild driftet. Lauf über den P2P-Steuerkanal (kein Server).
          const dc = dcRef.current;
          if (dc && dc.readyState === 'open') {
            try { dc.send(JSON.stringify({ type: 'camera.keyframe' } as CamCommand)); }
            catch (e) { console.warn('[rtc] keyframe-request an Stele fehlgeschlagen', e); }
          }
        }
        ls?.addTrack(newVideo);
        videoRawRef.current = ns;
        setPreferredDevice('videoinput', deviceId);
        console.log('[rtc] Kamera gewechselt →', deviceId);
      } catch (e) { setError(describeMediaError(e)); }
    },
    async switchAudioInput(deviceId: string) {
      const pc = pcRef.current;
      if (!pc) return;
      try {
        const na = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: deviceId } } });
        const newAudio = na.getAudioTracks()[0];
        if (!newAudio) { na.getTracks().forEach((t) => t.stop()); return; }
        const sender = pc.getSenders().find((s) => s.track?.kind === 'audio');
        if (sender) await sender.replaceTrack(newAudio);
        const ls = localStreamRef.current;
        ls?.getAudioTracks().forEach((t) => { try { ls.removeTrack(t); t.stop(); } catch {} });
        ls?.addTrack(newAudio);
        setPreferredDevice('audioinput', deviceId);
        console.log('[rtc] Mikrofon gewechselt →', deviceId);
      } catch (e) { setError(describeMediaError(e)); }
    },
    localStream,
    localStreamMain: localStream,
    localPortraitStream,
    remoteStream: remoteStreamMain, // Rückwärtskompatibel für InCall.tsx
    remoteStreamMain,
    remoteStreamPreview,
    remoteStreamAudio,
    remoteGuestStream,
    guestName,
    disconnectGuest,
    remoteMainMeta,
    remotePreviewMeta,
    hasPreview,
    capabilities,
    roiState,
    lastScan,
    lastSignature,
    submitSignature,
    abortSignature,
    connectionInfo,
    liveStats,
    hangup(reason = 'local-hangup') { teardown(reason); },
    /** Welle 7e: Aufzeichnungs-Status an die Stele melden (für den Gast-Indikator). */
    sendRecordingState(active: boolean, topic?: string) {
      socketRef.current?.emit('rtc:recording-state', { active, topic });
    },
    requestRecordingConsent(topic: string, mode: 'hinweis' | 'einwilligung', timeoutMs = 60000) {
      return new Promise<{ accepted: boolean; at: number; cancelled?: boolean }>((resolve) => {
        const sock = socketRef.current;
        if (!sock) { resolve({ accepted: false, at: Date.now() }); return; }
        const timer = setTimeout(() => {
          if (consentResolverRef.current) { consentResolverRef.current = null; resolve({ accepted: false, at: Date.now() }); }
        }, timeoutMs);
        consentResolverRef.current = (r) => { clearTimeout(timer); resolve(r); };
        sock.emit('rtc:recording-consent-request', { topic, mode });
      });
    },
    cancelRecordingConsent() {
      socketRef.current?.emit('rtc:recording-consent-cancel', {});
      const r = consentResolverRef.current; consentResolverRef.current = null;
      r?.({ accepted: false, at: Date.now(), cancelled: true });
    },
    toggleMic() {
      const t = localStreamRef.current?.getAudioTracks()[0];
      if (!t) return false;
      t.enabled = !t.enabled;
      return t.enabled;
    },
    toggleCamera() {
      const t = localStreamRef.current?.getVideoTracks()[0];
      if (!t) return false;
      t.enabled = !t.enabled;
      // Welle 6: portrait-Pipeline auch togglen, damit Canvas Schwarz rendert wenn aus
      portraitRef.current?.setEnabled(t.enabled);
      return t.enabled;
    },
    requestSwap() {
      const followMesh = localStorage.getItem('bamoa.swap.followMesh') === '1';
      console.log(`[rtc] requesting swap (followMesh=${followMesh})`);
      socketRef.current?.emit('rtc:swap', { followMesh });
    },
    sendCamCommand,
    previewEnabled,
    setPreviewEnabled,
  };
}
